
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { aiService } from '../services/aiService';
import { CharacterCard } from '../types';
import { useAgentOrchestration } from '../context/AgentOrchestrationContext';
import { errorHandler } from '../services/errorHandler';
import { ChatPersistence, Conversation } from '../services/chatPersistence';
import { HeuristicPopup, CodeBlock } from './HeuristicPopup';
import { authHeaders } from '../utils/auth';
import { BrowserTaskPopup } from './BrowserTaskPopup';
import { VideoProcessorPanel } from './VideoProcessorPanel';

declare global {
  interface AudioContextOptions {
    latencyHint?: number | 'balanced' | 'interactive' | 'playback';
    sampleRate?: number;
  }
}

// ── Multimedia support ─────────────────────────────────────────────────────
interface Attachment {
  id: string;
  name: string;
  type: 'image' | 'audio' | 'video' | 'pdf' | 'text' | 'file';
  mimeType: string;
  dataUrl: string;   // base64 data URL for rendering & sending
  size: number;
}

interface Message {
  role: 'user' | 'assistant' | 'system' | 'tool';
  content: string;
  rawContent?: string; // Original un-sanitized content for history
  contextContent?: string; // Compressed version for context (tool results, condensed)
  attachments?: Attachment[];
  toolCalls?: any[]; // raw tool calls from model
  toolCallId?: string; // id of the tool this message is responding to
  name?: string; // name of the tool this message is responding to
  isCondensed?: boolean; // flag for condensed summary messages
}

type ThinkingMemoryMode = 'off' | 'compact' | 'full';

interface ThinkingStep {
  id: string;
  session: number;
  thought: string;
  summary: string;
  timestamp: number;
  source: 'sequentialthinking' | 'fallback';
}

interface ThinkingSettings {
  enabled: boolean;
  sessions: number;
  memoryMode: ThinkingMemoryMode;
  autoCloseOnResponse: boolean;
}

interface AgentChatProps {
  onToolCall: (call: any) => void;
  onConversationStateChange?: (isActive: boolean) => void; // Phase 1.4: Notify parent
  addLog?: (level: 'INFO' | 'WARN' | 'ERROR' | 'DEBUG' | 'AI_ACTION', message: string, component?: string) => void;
  auditMode?: boolean;
}

// Default system prompt
const DEFAULT_SYSTEM_PROMPT = `JESTEŚ ARCHITEKTEM NEURONOWYM (SYSTEM OMNIGATE).
Twoim celem jest zarządzanie infrastrukturą AI i pomoc operatorowi.

═══════════════════════════════════════════════════
ZASADY OGÓLNE
═══════════════════════════════════════════════════
1. Odpowiadaj ZAWSZE po polsku. Bądź konkretny i techniczny.
2. NIE HALUCYNUJ NARZĘDZI. Używaj tylko narzędzi MCP dostępnych w liście.
3. MOMENTUM: Po każdym wyniku narzędzia — NATYCHMIAST kontynuuj plan. Nie pytaj o zgodę przy każdym kroku. Wykonaj sekwencję do końca, potem raportuj.

═══════════════════════════════════════════════════
MAPA NARZĘDZI (SKILLS)
═══════════════════════════════════════════════════
Masz dostęp do następujących kategorii:

[SYSTEM]
  • list_processes       — lista procesów Linux
  • system_metrics       — CPU/RAM/dysk
  • read_file / write_file / list_directory — filesystem
  • execute_command      — shell

[DOCKER]
  • docker_list / docker_start / docker_stop / docker_logs

[AI/MODELE]
  • ollama_list_models / ollama_pull / ollama_delete
  • list_running_models

[PRZEGLĄDARKA — PLAYWRIGHT]
  browser_navigate, browser_snapshot, browser_take_screenshot,
  browser_click, browser_type, browser_fill_form, browser_hover,
  browser_select_option, browser_evaluate, browser_network_requests,
  browser_console_messages, browser_navigate_back, browser_tabs,
  browser_wait_for, browser_resize, browser_close, browser_handle_dialog

[PRZEGLĄDARKA — AGENT AI]
  • run_browser_agent    — autonomiczny agent webowy

═══════════════════════════════════════════════════
PRZEGLĄDARKA — HEURYSTYKI I ZASADY
═══════════════════════════════════════════════════

## FUNDAMENTALNA ZASADA: PĘTLA SNAPSHOT → ANALIZA → AKCJA

NIGDY nie zakładaj jak wygląda strona. Po każdej akcji MUSISZ zrobić snapshot
i przeczytać wynik ZANIM zdecydujesz co zrobić dalej.

  ┌─────────────────────────────────────────────────────┐
  │  PEŁNA PĘTLA NAWIGACJI (obowiązkowa dla każdego kroku):      │
  │                                                     │
  │  1. browser_navigate(url)                           │
  │  2. browser_snapshot({})     ← CZYTAJ wynik         │
  │  3. PRZEANALIZUJ snapshot:                          │
  │     - czy jest dialog/modal/overlay?                │
  │     - jakie są dostępne buttony/inputy/linki?       │
  │     - jaki jest aktualny stan strony?               │
  │  4. WYKONAJ jedną akcję (click/type/etc.)           │
  │  5. browser_snapshot({})     ← CZYTAJ wynik         │
  │  6. OCEŃ: czy akcja przyniosła oczekiwany efekt?    │
  │  7. WRÓĆ do kroku 3 → powtarzaj aż do celu         │
  └─────────────────────────────────────────────────────┘

  ⚠️  browser_snapshot NIE przyjmuje żadnych argumentów:
      BŁĄD: browser_snapshot(element="...", ref="e4")  ← NIEPOPRAWNE
      OK:   browser_snapshot({})                        ← POPRAWNE

  ❌ ZAKAZANE PODEJŚCIE (nie rób tak):
     - Zakładanie tekstu przycisków bez czytania snapshota
     - Wpisywanie wyszukiwanej frazy bezpośrednio w URL (np. ?q=iphone)
       zamiast znaleźć pole search i wpisać przez browser_type
     - Wielokrotne browser_navigate zamiast klikania linków z ref=
     - Zakładanie że popup jest zamknięty bez weryfikacji w snapshot

## Kiedy używać którego narzędzia?
- Krok-po-kroku z analizą snapshota → PLAYWRIGHT (browser_*) + pętla powyżej
- Strony blokujące boty (Allegro, Amazon, e-commerce) → run_browser_agent
- NIE używaj run_browser_agent do prostych stron — kosztuje czas VL modelu.

## OBSŁUGA POP-UPÓW (ZAWSZE przez snapshot, NIE przez zakładanie tekstu):
Pop-upy blokują kliknięcia — ZAWSZE sprawdź snapshot po navigate i obsłuż dialogi.

  METODA LANGUAGE-AGNOSTIC (działa na każdym języku strony):

  KROK 1: Po browser_snapshot sprawdź czy snapshot zawiera:
    - role=dialog  lub  aria-modal=true  lub  role=alertdialog
    → Jeśli TAK: masz pop-up do zamknięcia

  KROK 2: W dialogu znajdź przycisk "ACCEPT" (pierwszy/główny/wyróżniony button):
    - Nie szukaj po tekście ("Zgadzam się" / "I Agree" / "Accetto" / "同意" — nieznane)
    - Szukaj po POZYCJI i ROLI:
      → Pierwszy button w role=dialog (zazwyczaj = accept/zgadzam się)
      → Lub button z aria-label zawierającym "accept|agree|consent|ok|allow"
      → Lub OSTATNI button jeśli pierwszy to "Reject/Odrzuć/Decline"
    → browser_click(ref="<ref tego buttona>", element="accept button in dialog [ref=eX]")

  KROK 3: Natywne dialogi przeglądarki (powiadomienia push, lokalizacja):
    → browser_handle_dialog(accept=false)   ← ZAWSZE odrzucaj

  KROK 4: Overlay blokujący stronę po zamknięciu dialogu:
    → browser_evaluate("document.querySelector('[aria-modal],[role=dialog]')?.remove()")

  KROK 5: Po każdym zamknięciu pop-upu:
    → browser_snapshot({}) PONOWNIE → sprawdź czy strona jest już dostępna

  DLACZEGO NIE ZAKŁADAMY TEKSTU PRZYCISKÓW:
  - Każda strona ma inny język, inny komponent UI, inny tekst
  - "Zgadzam się" (PL) = "I Agree" (EN) = "Accetto" (IT) = "Ich stimme zu" (DE)
  - Snapshot pokazuje DOKŁADNIE co jest na ekranie — czytaj go zamiast zgadywać

## HEURYSTYKA ref= — NAJWAŻNIEJSZA ZASADA:
Po otrzymaniu snapshota YAML, wybierz ref= według priorytetu:

  PRIORYTET 1 — INTERAKTYWNE ROLE (używaj zawsze jeśli dostępne):
    textbox      → dla browser_type (pola tekstowe, input, textarea)
    combobox     → dla browser_type lub browser_select_option (autocomplete, search)
    searchbox    → dla browser_type (pole wyszukiwania)
    button       → dla browser_click (przyciski)
    checkbox     → dla browser_click (checkboxy)
    radio        → dla browser_click (radio buttony)
    link         → dla browser_click (linki <a>)
    select       → dla browser_select_option (dropdowny <select>)

  PRIORYTET 2 — STRUKTURALNE (używaj gdy brak interaktywnych):
    heading, paragraph, listitem, row, cell

  PRIORYTET 3 — UNIKAJ:
    generic, main, article, section, div, nav, footer
    → NIE używaj generic/main jako target dla type/click, chyba że to jedyna opcja.

## Przykład poprawnego wyboru ref:
  YAML:
    - generic [ref=e4]:
        - text: "Customer name:"
        - textbox "Customer name:" [ref=e5]
  ✅ POPRAWNE: browser_type(ref="e5", element="textbox Customer name: [ref=e5]")
  ❌ BŁĘDNE:   browser_type(ref="e4") ← generic, nie przyjmuje tekstu

## Jak szukać w dużym snapshota:
  Szukaj słów kluczowych: "textbox", "combobox", "button", "searchbox"
  Ignoruj linie z "generic", "paragraph", "group" przy wyborze ref= dla type/click.

═══════════════════════════════════════════════════
MODEL VISION — WAŻNE OGRANICZENIE
═══════════════════════════════════════════════════
NIE jesteś modelem vision. NIE widzisz obrazów ani base64 PNG.
• browser_snapshot = JEDYNA metoda "widzenia" strony (drzewo tekstowe ARIA)
• browser_take_screenshot = tylko dla użytkownika, nie dla ciebie
• NIGDY nie pisz "SNAPSHOT" jako tekstu — wywołaj narzędzie browser_snapshot.

═══════════════════════════════════════════════════
DOBÓR URL DLA DANYCH W CZASIE RZECZYWISTYM
═══════════════════════════════════════════════════
## Pogoda — ZAWSZE używaj wttr.in (nie weather.com!):
  weather.com jest wolna, wymaga kliknięć, przekierowuje na Warszawę.
  wttr.in zwraca dane w <1s bez kliknięć.

  DOZWOLONE FORMATY wttr.in (TYLKO te — nie wymyślaj innych!):
  ┌─────────────────────────────────────────────────────────────────┐
  │  ?format=3   → jeden wiersz: "Grudziądz: ☀️ +15°C"            │
  │  ?format=j1  → PEŁNY JSON: aktualne dane + prognoza 3 dni      │
  │               (weather[0]=dziś, weather[1]=jutro, weather[2])  │
  │  ?format=j2  → JSON z lokalizacją (rzadziej potrzebny)         │
  └─────────────────────────────────────────────────────────────────┘

  ❌ NIE ISTNIEJĄ: format=7, format=4, format=5, format=6, format=json
     NIE dodawaj &lang= — wttr.in ignoruje ten parametr dla j1

  ✅ Aktualna pogoda (szybkie):
     browser_navigate("https://wttr.in/Grudziadz?format=3")
     browser_snapshot()

  ✅ Pełna prognoza + szczegóły (jeden call, zawiera 3 dni w JSON):
     browser_navigate("https://wttr.in/Grudziadz?format=j1")
     browser_snapshot()
     → Parse: current_condition[0].temp_C = temperatura
     → Parse: weather[0] = dziś, weather[1] = jutro, weather[2] = pojutrze
     → Każdy weather[N] ma: hourly[].tempC, hourly[].weatherDesc, maxtempC, mintempC

  Dla polskich miast — używaj ASCII (Grudziadz, Gdansk, Wroclaw, Krakow):
    NIE używaj znaków %C4%85 w run_browser_agent, bo VL model może je pomylić

## Kursy walut / inne API:
  exchange rate: browser_navigate("https://api.exchangerate.host/latest?base=PLN")
  IP info:       browser_navigate("https://ipapi.co/json")

## Allegro i inne sklepy e-commerce — ANTY-BOT:
  Allegro/Amazon/Ceneo wykrywają automatyczne requesty i zwracają 403 + CAPTCHA.
  NIE używaj browser_navigate bezpośrednio — będziesz blokowany.
  ZAWSZE używaj run_browser_agent z opisem zadania zawierającym URL:

  Poprawny URL wyszukiwania Allegro (format /listing, NIE /q/):
    https://allegro.pl/listing?string=ZAPYTANIE&stan[]=uzywane&order=p
  Przykłady:
    iPhone 15 Pro Max używ. najtańsze: https://allegro.pl/listing?string=iphone+15+pro+max&stan[]=uzywane&order=p
    Nowe laptopy dell:                 https://allegro.pl/listing?string=laptop+dell&stan[]=nowe&order=p

  ✅ Poprawne wywołanie dla Allegro (VL model sam obsłuży pop-up przez screenshot):
    run_browser_agent task="Open https://allegro.pl/listing?string=iphone+15+pro+max&stan[]=uzywane&order=p
    Wait for page to load. If a cookie consent dialog appears, click the accept/consent button (it may say
    'Zgadzam się', 'Accept', 'Akceptuję' — click whichever accept button is visible in the dialog).
    Then extract the first 5 product listings: title, price, URL. Return as JSON list."

  ❌ NIE DZIAŁA (bot detection):
    browser_navigate('https://allegro.pl/...')  ← zwróci 403 lub pusty wynik

═══════════════════════════════════════════════════
ZŁOŻONE ZADANIA — FUNDAMENTALNA LOGIKA DZIAŁANIA
═══════════════════════════════════════════════════

## ⛔ ANTI-LOOP: JEŚLI NARZĘDZIE ZWRÓCI BŁĄD — STOP

  Jeśli tool_result zawiera "Unknown tool", "not found", "Skill ... not found",
  "error": true lub jakikolwiek błąd braku narzędzia:
    → NATYCHMIAST STOP — nie wywołuj tego narzędzia PONOWNIE
    → Sprawdź dostępną listę narzędzi i wybierz właściwą alternatywę
    → Jeśli nie ma alternatywy — poinformuj użytkownika o ograniczeniu

  ❌ PĘTLA = BUG: list_apps → adb_shell (fail) → list_apps → adb_shell → ...
  ✅ POPRAWNIE:  list_apps → (fail: adb_shell) → STOP → użyj press_key zamiast tego

## ⛔ ANTI-LOOP: PĘTLA STRATEGII — RÓWNIE GROŹNA

  Jeśli wykonałeś tę samą sekwencję kroków 2+ razy bez postępu:
    → ZATRZYMAJ SIĘ
    → Przejrzyj historię — co robiłeś? Czy to działało?
    → Zmień strategię całkowicie
    → Jeśli nie wiesz co robić — opisz problem użytkownikowi i zapytaj

  Przykładowe pętle strategii do zidentyfikowania i zatrzymania:
    ❌ screenshot → press_key(HOME) → browser_navigate → browser_close → repeat
    ❌ screenshot → tap(X,Y) → screenshot (ten sam widok) → tap(X,Y) → repeat
    ❌ open_app → screenshot → open_app → screenshot → repeat

  Jeśli widzisz IDENTYCZNY wynik screenshota 2 razy z rzędu →
  UI się NIE zmienia → poprzednia akcja nie zadziałała → zmień podejście.

## ZASADA NACZELNA: NIGDY NIE DZIAŁAJ W CIEMNO

  Każde zadanie wymagające interakcji z UI (przeglądarka, telefon, aplikacja)
  MUSI być wykonane przez pętlę: OBSERWUJ → ROZUMIEJ → PLANUJ → DZIAŁAJ → WERYFIKUJ

  ❌ ZABRONIONE: "Wykonam całe zadanie sekwencją komend i zobaczę co wyjdzie"
  ✅ WYMAGANE:   Każdy krok oparty na tym co AKTUALNIE widzisz na ekranie

## WZORZEC PĘTLI (obowiązuje wszędzie: phone, browser, desktop):

  ┌─────────────────────────────────────────────────────────┐
  │  PĘTLA WYKONANIA ZŁOŻONEGO ZADANIA                      │
  │                                                         │
  │  1. SNAPSHOT — zrób screenshot / zrób snapshot strony   │
  │  2. ANALIZA  — co widzę? jaki jest stan? co jest gdzie? │
  │  3. DECYZJA  — co jest następnym krokiem w stronę celu? │
  │  4. AKCJA    — wykonaj JEDNĄ akcję (tap/click/type)     │
  │  5. SNAPSHOT — zrób screenshot PONOWNIE                 │
  │  6. WERYFIKACJA — czy akcja przyniosła oczekiwany efekt?│
  │     → TAK: przejdź do następnego kroku                  │
  │     → NIE: zidentyfikuj przeszkodę (pop-up? błąd?)     │
  │             obsłuż ją i wróć do kroku 1                 │
  └─────────────────────────────────────────────────────────┘

## ZASADA "JEDNA AKCJA = JEDEN SCREENSHOT":
  Po każdym: tap, swipe, input text, keyevent, click — ZAWSZE wykonaj screenshot.
  Nigdy nie wykonuj 2+ akcji bez sprawdzenia efektu pierwszej.

## WERYFIKACJA TEKSTU PRZED WYSŁANIEM (krytyczne!):

  Gdy zadanie wymaga wpisania tekstu i wysłania:
    KROK A: tap(x=X, y=Y)           → kliknij pole tekstowe
    KROK B: screenshot()             → sprawdź że pole jest aktywne
    KROK C: type_text(text='txt')   → wpisz tekst
    KROK D: screenshot()             → SPRAWDŹ ŹE TEKST JEST POPRAWNY
             → Czy tekst jest widoczny w polu?
             → Czy wkleił się poprawnie? (brak znaków specjalnych?)
             → Czy jest uzupełnianie / podpowiedź? (może być potrzebny tap na nią)
    KROK E: DOPIERO TERAZ → tap(x=SEND_X, y=SEND_Y)
    KROK F: screenshot()             → potwierdź wysłanie

  ⚠️ NIGDY nie wysyłaj "na ślepo" bez weryfikacji krok D!
     Klawiatura telefonu może: przestawić znaki, autokorygować, nie przyjąć tekstu

## OBSŁUGA NIESPODZIEWANYCH STANÓW:
  Jeśli po akcji screenshot pokazuje coś nieoczekiwanego:
    - Pop-up/dialog: zidentyfikuj zawartość, wciśnij właściwy przycisk, kontynuuj
    - Inne menu/screen: nawiguj back (KEYCODE_BACK / tap strzałki wstecz)
    - Błąd/crash: opisz błąd użytkownikowi z dokładnym screenshotem
    - Klawiatura zasłoniła przycisk: sprawdź nowe współrzędne (UI się przesuwa!)

## KLAWIATURA ZMIENIA UKŁAD EKRANU — WAŻNE:
  Gdy klawiatura jest aktywna, elementy UI przesuwają się w GÓRĘ.
  Przycisk "wyślij" / "OK" który normalnie jest na dole, może być teraz w środku ekranu.
  Zawsze sprawdzaj screenshot po pojawieniu się klawiatury!

═══════════════════════════════════════════════════
ANDROID — STEROWANIE PRZEZ ADB
═══════════════════════════════════════════════════

## ⛔ TELEGRAM NA ANDROID — BEZWZGLĘDNE ZAKAZY:

  ❌ NIE używaj browser_navigate do zadań na urządzeniu Android
  ❌ NIE używaj Telegram Bot HTTP API (api.telegram.org) — to nie jest bot, to aplikacja na telefonie
  ❌ NIE używaj browser_navigate, run_browser_agent, ani żadnego narzędzia WWW
     do sterowania aplikacją na urządzeniu Android

  ✅ JEDYNA PRAWIDŁOWA METODA = ADB:
     open_app() → screenshot() + analiza → tap() → type_text() → tap()

  Jeśli widzisz pokusę użycia browser_navigate dla zadania Android → STOP.
  To zawsze błąd. Użyj screenshot() żeby zobaczyć co jest na ekranie,
  potem tap() żeby kliknąć. Kropka.

## JAK ANALIZOWAĆ SCREENSHOT:

  Wynik screenshot() zawiera pole "image_base64" — to OBRAZ EKRANU TELEFONU.
  Obraz ma rozmiar 720×1480px. Każdy piksel odpowiada współrzędnej ADB 1:1.

  Po otrzymaniu screenshota:
    1. OPISZ co widzisz (jaki ekran, jakie elementy, gdzie są)
    2. ZLOKALIZUJ element który chcesz kliknąć (podaj X, Y)
    3. WYWOŁAJ tap(x=X, y=Y)
    4. Zrób kolejny screenshot → sprawdź efekt

  NIE zgaduj — patrz na obraz i czytaj współrzędne z tego co widzisz.

## DOSTĘPNE NARZĘDZIA ANDROID (DOKŁADNE NAZWY — używaj tylko tych):

  list_devices()                          → lista podłączonych urządzeń ADB
  screenshot(device_id?)                  → zrzut ekranu → ANALIZA WIZUALNA
  tap(x, y, device_id?)                   → kliknięcie w punkt ekranu
  swipe(x1, y1, x2, y2, device_id?)      → gest swipe
  type_text(text, device_id?)             → wpisanie tekstu do aktywnego pola
  press_key(keycode, device_id?)          → keyevent ("3"=HOME, "4"=BACK, "66"=ENTER)
  open_app(package, device_id?)           → uruchomienie aplikacji po pakiecie
  list_apps(device_id?)                   → lista zainstalowanych aplikacji
  get_screen_size(device_id?)             → rozmiar ekranu
  battery(device_id?)                     → stan baterii
  network_info(device_id?)               → informacje sieciowe

  ❌ NIE ISTNIEJĄ (nie wywołuj!): adb_shell, adb_screenshot, adb_tap,
     adb_type, shell_execute, shell_command, run_adb, adb_command
     → Wywołanie będzie skutkować "Unknown tool" → pętla błędów → STOP

## FUNDAMENTALNE OGRANICZENIE — ADB BEZ VISION = ŚLEPY AGENT:

  Możesz:
    ✅ tap(x=X, y=Y)              → kliknięcie w ZNANE współrzędne
    ✅ type_text(text='hello')    → wpisanie tekstu do aktywnego pola
    ✅ list_apps()                → lista zainstalowanych aplikacji

  Nie możesz (bez screenshota + analizy):
    ❌ Wiedzieć gdzie są przyciski na ekranie
    ❌ Czytać zawartości ekranu
    ❌ Reagować na dynamiczne UI (pop-upy, dialogi, nawigacja)
    ❌ Znać aktualnego stanu aplikacji

## ⚠️ KLUCZOWA REGUŁA — JAK OTWORZYĆ TELEGRAM:

  Telegram MOŻE nie być widoczny jako ikona na ekranie głównym.
  NIE szukaj ikony. NIE wciskaj HOME wielokrotnie.

  JEDYNA POPRAWNA PROCEDURA:
    KROK 1: open_app(package="org.telegram.messenger")   ← ZAWSZE PIERWSZY KROK!
    KROK 2: screenshot()                                  → sprawdź czy Telegram się otworzył
    KROK 3: jeśli nie otworzył się → list_apps() → szukaj "telegram" → użyj znalezionego pakietu

  ❌ ZAKAZANE: press_key(keycode="3") w celu otwarcia Telegram — HOME nie otwiera aplikacji!
  ❌ ZAKAZANE: szukanie ikony Telegram na home screen przed wywołaniem open_app()

## KOMPLETNY PRZYKŁAD — WYSŁANIE WIADOMOŚCI TELEGRAM (działa!):

  Cel: "Wyślij /status do użytkownika wrun w Telegram"

  KROK 1: open_app(package="org.telegram.messenger")    → uruchom Telegram
           (SLEEP 2s)
  KROK 2: screenshot()                              → widzę: lista czatów lub bubble
           → bubble widoczna? → tap(x=487, y=397)  → expand do pełnej aplikacji
           → lista czatów widoczna? → przejdź do KROK 4
  KROK 3: screenshot()                              → widzę: pełna aplikacja Telegram
           → tap(x=47, y=91)                        → back button → lista czatów
  KROK 4: screenshot()                              → widzę: lista, "wrun" → odczytaj coords
           → tap(x=360, y=285)                      → otwórz czat wrun (użyj rzeczywistych coords!)
  KROK 5: screenshot()                              → widzę: czat wrun, pole input
           → tap(x=390, y=1340)                     → aktywuj pole tekstowe
           (SLEEP 0.5s)
  KROK 6: type_text(text="/status")
  KROK 7: screenshot()                              → WERYFIKACJA TEKSTU!
           → "/status" widoczne w polu? → TAK
           → klawiatura aktywna → przycisk wyślij przesunięty!
  KROK 8: tap(x=667, y=743)                        → tap na przycisk wyślij (sprawdź coords ze screenshota!)
           (SLEEP 4s — czekaj na odpowiedź bota)
  KROK 9: screenshot()                              → ODCZYT ODPOWIEDZI
           → bot odpowiedział? → odczytaj treść → raportuj użytkownikowi

  ✅ WYNIK: "/status" wysłany, odpowiedź odczytana i przekazana użytkownikowi

## ⚠️ KLUCZOWE: ROZPOZNAWANIE STANU EKRANU — CO ROBIĆ DALEJ:

  Jeśli screenshot pokazuje:

  1️⃣ "Telegram chat with wrun open" LUB "wrun" + "Napisz wiadomość" (input field):
     → JESTEŚ JUŻ W CZACIE WRUN! Nie wywołuj open_app()!
     → NATYCHMIAST: tap(x=INPUT_X, y=INPUT_Y)  ← coords z VLM opisu
     → Następnie: type_text(text="/models")
     → Następnie: screenshot() → znajdź przycisk Wyślij → tap()

  2️⃣ "Telegram chat" (inny użytkownik) LUB lista czatów Telegram:
     → szukaj "wrun" na liście → tap() na wrun coords z VLM opisu
     → NIE wywołuj open_app()!

  3️⃣ "Telegram app open" + lista czatów / главный экран Telegram:
     → szukaj "wrun" → tap()
     → NIE wywołuj open_app()!

  4️⃣ Home screen (bez Telegram):
     → open_app(package="org.telegram.messenger")
     → raz — potem screenshot, NIGDY więcej open_app z rzędu bez screenshota

  ❌ BEZWZGLĘDNIE ZAKAZANE: open_app() wywołane 2+ razy z rzędu bez tap() między nimi!
     → To jest pętla błędu. Jeśli open_app() już zostało wywołane → SPRAWDŹ screenshot → DZIAŁAJ!

## ZADANIA MOŻLIWE BEZ VISION (proste, nie wymagają UI):
  - Uruchomienie aplikacji po pakiecie: open_app(package="org.telegram.messenger")
  - Lista aplikacji: list_apps()
  - Dane baterii: battery()
  - Rozmiar ekranu: get_screen_size()
  - Informacje sieciowe: network_info()
  - Lista urządzeń: list_devices()

═══════════════════════════════════════════════════
MOMENTUM PATTERN (wzorzec wykonania)
═══════════════════════════════════════════════════
Gdy dostajesz zadanie wielokrokowe:
  KROK 1 → wywołaj narzędzie → poczekaj na wynik → PRZEANALIZUJ wynik
  KROK 2 → na podstawie wyniku → zdecyduj → wywołaj następne narzędzie
  KROK N → raportuj wynik użytkownikowi (jeden raport na końcu)

NIE pytaj "czy mam kontynuować?" między krokami — kontynuuj autonomicznie.
NIE zakładaj stanu ekranu — ZAWSZE sprawdź screenshotem.
NIE wysyłaj tekstu bez weryfikacji że tekst jest poprawny w polu.

⚡ KRYTYCZNA ZASADA KONTYNUACJI NARZĘDZI:
Po otrzymaniu wyniku narzędzia [Wynik narzędzia X]: ... — NATYCHMIAST wywołaj
kolejne narzędzie tagiem <tool_call> jeśli zadanie nie jest ukończone.
ZAKAZ opisywania planów słowami po wyniku — od razu działaj!
  ✅ [Wynik narzędzia foo]: OK  →  <tool_call>{"name": "bar", ...}</tool_call>
  ❌ [Wynik narzędzia foo]: OK  →  "Teraz sprawdzę dokumentację API..."

═══════════════════════════════════════════════════
GŁOS
═══════════════════════════════════════════════════
Jeśli "VOICE ON": używaj naturalnej interpunkcji i krótkich zdań.

TWOJA OSOBOWOŚĆ:
Jesteś inteligentnym, proaktywnym inżynierem systemu. Nie jesteś "zwykłym chatbotem". Jesteś interfejsem OmniGate — działasz, nie tylko mówisz.`;

// ── Context Window helpers ─────────────────────────────────────────────────
const CHARS_PER_TOKEN = 4; // approx estimate
const estimateTokens = (text: string) => Math.ceil(text.length / CHARS_PER_TOKEN);
// Use contextContent for token counting if available (compressed version)
const estimateMessageTokens = (m: Message) => estimateTokens(m.contextContent || m.rawContent || m.content);

// Max chars for tool results stored in context (display shows full)
const MAX_TOOL_RESULT_CONTEXT_CHARS = 2500; // zwiększone: 1200 było za agresywne, model tracił kontekst wyników narzędzi

// ── Translation Popup Component ──────────────────────────────────────────────
const TRANSLATION_LANGUAGES = [
  { code: 'auto', label: 'Auto' },
  { code: 'pl', label: 'PL' },
  { code: 'en', label: 'EN' },
  { code: 'de', label: 'DE' },
  { code: 'fr', label: 'FR' },
  { code: 'es', label: 'ES' },
  { code: 'it', label: 'IT' },
  { code: 'pt', label: 'PT' },
  { code: 'ru', label: 'RU' },
  { code: 'uk', label: 'UK' },
  { code: 'cs', label: 'CS' },
  { code: 'zh', label: 'ZH' },
  { code: 'ja', label: 'JA' },
  { code: 'ko', label: 'KO' },
  { code: 'ar', label: 'AR' },
  { code: 'tr', label: 'TR' },
];

type TranslationStatus = 'idle' | 'detecting' | 'translating' | 'done' | 'error';

interface TranslationPopupProps {
  visible: boolean;
  onClose: () => void;
  originalText: string;
  translatedText: string;
  streamingText: string;
  status: TranslationStatus;
  sourceLang: string;
  targetLang: string;
  detectedLang?: string;
  durationMs?: number;
  glossaryApplied?: boolean;
  onCopyToInput: (text: string) => void;
  onAddGlossary: (sourceWord: string, targetWord: string) => void;
}

const TranslationPopup: React.FC<TranslationPopupProps> = ({
  visible, onClose, originalText, translatedText, streamingText,
  status, sourceLang, targetLang, detectedLang, durationMs,
  glossaryApplied, onCopyToInput, onAddGlossary
}) => {
  const [editedText, setEditedText] = useState(translatedText);
  const [glossaryWord, setGlossaryWord] = useState('');
  const [glossaryCorrected, setGlossaryCorrected] = useState('');
  const [glossaryMsg, setGlossaryMsg] = useState('');
  const [showGlossary, setShowGlossary] = useState(false);

  // Sync editedText when translatedText changes (on each completed translation)
  useEffect(() => { if (status === 'done') setEditedText(translatedText); }, [translatedText, status]);

  if (!visible) return null;

  const statusColors: Record<TranslationStatus, string> = {
    idle: 'text-slate-500',
    detecting: 'text-yellow-400',
    translating: 'text-blue-400',
    done: 'text-emerald-400',
    error: 'text-red-400',
  };
  const statusLabels: Record<TranslationStatus, string> = {
    idle: 'Gotowy',
    detecting: 'Wykrywam język...',
    translating: 'Tłumaczę...',
    done: `Gotowe${durationMs ? ` (${durationMs}ms)` : ''}`,
    error: 'Błąd tłumaczenia',
  };

  const displayText = status === 'done' ? translatedText : streamingText;

  const handleAddGlossary = async () => {
    if (!glossaryWord.trim() || !glossaryCorrected.trim()) return;
    onAddGlossary(glossaryWord.trim(), glossaryCorrected.trim());
    setGlossaryMsg(`✓ Dodano: "${glossaryWord}" → "${glossaryCorrected}"`);
    setGlossaryWord(''); setGlossaryCorrected('');
    setTimeout(() => setGlossaryMsg(''), 3000);
  };

  return (
    <div className="absolute inset-0 z-50 pointer-events-none flex items-start justify-end">
      <div className="pointer-events-auto mt-12 mr-2 w-[420px] max-w-[calc(100vw-1rem)] bg-[#0d1117] border border-blue-500/30 rounded-lg shadow-2xl flex flex-col max-h-[80vh]" style={{backdropFilter:'blur(8px)'}}>
        {/* Header */}
        <div className="px-3 py-2 border-b border-blue-500/20 flex items-center justify-between bg-gradient-to-r from-blue-900/30 to-indigo-900/20 rounded-t-lg">
          <div className="flex items-center gap-2">
            <i className="fas fa-language text-blue-400 text-sm"></i>
            <span className="text-[10px] font-black uppercase tracking-widest text-blue-300">Translator</span>
            <span className={`text-[8px] font-bold ${statusColors[status]}`}>
              {detectedLang && status !== 'idle' ? `→ ${detectedLang.toUpperCase()}` : ''}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-[8px] ${statusColors[status]}`}>{statusLabels[status]}</span>
            {glossaryApplied && <span className="text-[7px] bg-emerald-500/20 text-emerald-400 px-1 py-0.5 rounded">glossary</span>}
            <button onClick={onClose} className="text-slate-500 hover:text-white text-xs"><i className="fas fa-times"></i></button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto custom-scrollbar p-3 space-y-3 min-h-0">
          {/* Original */}
          {originalText && (
            <div>
              <div className="text-[7px] uppercase tracking-widest text-slate-500 mb-1">
                <i className="fas fa-pen-alt mr-1"></i>Oryginał ({sourceLang === 'auto' ? (detectedLang || 'auto') : sourceLang})
              </div>
              <div className="text-[9px] text-slate-400 bg-[#131722] rounded p-2 border border-[#2a2e39] whitespace-pre-wrap max-h-24 overflow-y-auto">{originalText}</div>
            </div>
          )}

          {/* Translation */}
          <div>
            <div className="text-[7px] uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1">
              <i className="fas fa-globe mr-1"></i>Tłumaczenie ({targetLang})
              {(status === 'translating') && <i className="fas fa-circle-notch fa-spin text-blue-400 ml-1"></i>}
            </div>
            {status === 'done' ? (
              <textarea
                value={editedText}
                onChange={e => setEditedText(e.target.value)}
                className="w-full text-[9px] text-white bg-[#131722] rounded p-2 border border-blue-500/30 min-h-[60px] max-h-40 resize-y outline-none focus:border-blue-400/60 transition-colors custom-scrollbar"
                placeholder="Tłumaczenie pojawi się tutaj..."
              />
            ) : (
              <div className="text-[9px] text-slate-200 bg-[#131722] rounded p-2 border border-[#2a2e39] min-h-[40px] max-h-40 overflow-y-auto whitespace-pre-wrap">
                {displayText || <span className="text-slate-600 italic">Oczekiwanie...</span>}
                {status === 'translating' && <span className="inline-block w-1.5 h-3 bg-blue-400 animate-pulse ml-0.5 align-middle"></span>}
              </div>
            )}
          </div>

          {/* Action buttons */}
          {status === 'done' && (
            <div className="flex gap-1.5 flex-wrap">
              <button
                onClick={() => onCopyToInput(editedText)}
                className="flex items-center gap-1 px-2 py-1 rounded bg-blue-600/20 border border-blue-500/40 text-blue-400 text-[8px] hover:bg-blue-600/40 transition-all"
              >
                <i className="fas fa-arrow-up"></i> Do chatu
              </button>
              <button
                onClick={() => navigator.clipboard?.writeText(editedText)}
                className="flex items-center gap-1 px-2 py-1 rounded bg-slate-700/50 border border-[#2a2e39] text-slate-400 text-[8px] hover:text-white transition-all"
              >
                <i className="fas fa-copy"></i> Kopiuj
              </button>
              <button
                onClick={() => setShowGlossary(v => !v)}
                className={`flex items-center gap-1 px-2 py-1 rounded border text-[8px] transition-all ${
                  showGlossary ? 'bg-emerald-600/20 border-emerald-500/40 text-emerald-400' : 'bg-slate-700/50 border-[#2a2e39] text-slate-400 hover:text-white'
                }`}
              >
                <i className="fas fa-book"></i> Korekta
              </button>
            </div>
          )}

          {/* Glossary panel */}
          {showGlossary && (
            <div className="border border-emerald-500/20 rounded bg-emerald-900/10 p-2 space-y-2">
              <div className="text-[7px] uppercase tracking-widest text-emerald-400 font-bold">
                <i className="fas fa-book mr-1"></i>Korekta słownikowa
              </div>
              <div className="text-[7px] text-slate-500">Zaznacz błędne słowo i dodaj poprawną wersję — zostanie zapamiętana na przyszłość.</div>
              <div className="flex gap-1">
                <input
                  value={glossaryWord}
                  onChange={e => setGlossaryWord(e.target.value)}
                  placeholder="Słowo w oryginale/tłumaczeniu"
                  className="flex-1 bg-[#131722] border border-[#2a2e39] rounded px-2 py-1 text-[8px] text-slate-200 outline-none focus:border-emerald-500/50"
                />
                <span className="text-slate-500 self-center text-[8px]">→</span>
                <input
                  value={glossaryCorrected}
                  onChange={e => setGlossaryCorrected(e.target.value)}
                  placeholder="Poprawna wersja"
                  className="flex-1 bg-[#131722] border border-[#2a2e39] rounded px-2 py-1 text-[8px] text-slate-200 outline-none focus:border-emerald-500/50"
                />
                <button
                  onClick={handleAddGlossary}
                  className="px-2 py-1 rounded bg-emerald-600/30 border border-emerald-500/40 text-emerald-400 text-[8px] hover:bg-emerald-600/50 transition-all"
                >
                  <i className="fas fa-plus"></i>
                </button>
              </div>
              {glossaryMsg && <div className="text-[7px] text-emerald-400">{glossaryMsg}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── ToolResultDisplay ─────────────────────────────────────────────────────────
const TOOL_RESULT_PREVIEW_CHARS = 400;

const extractAudioUrlsFromToolResult = (content: string): string[] => {
  const urls = new Set<string>();
  const addUrl = (value: unknown) => {
    if (typeof value !== 'string') return;
    const trimmed = value.trim();
    if (!trimmed) return;
    if (
      /^https?:\/\//i.test(trimmed) ||
      trimmed.startsWith('/v1/acestep/media') ||
      /\.(mp3|wav|flac|ogg|opus|aac|m4a)(\?|#|$)/i.test(trimmed)
    ) {
      urls.add(trimmed);
    }
  };

  const walk = (value: unknown) => {
    if (!value) return;
    if (typeof value === 'string') {
      addUrl(value);
      return;
    }
    if (Array.isArray(value)) {
      value.forEach(walk);
      return;
    }
    if (typeof value === 'object') {
      const obj = value as Record<string, unknown>;
      ['audio_url', 'audioUrl', 'url', 'proxy_url', 'file_url', 'path', 'name'].forEach(key => addUrl(obj[key]));
      Object.values(obj).forEach(walk);
    }
  };

  try {
    walk(JSON.parse(content));
  } catch {
    const matches = content.match(/(?:https?:\/\/|\/v1\/acestep\/media\?)[^\s"'<>]+/gi) || [];
    matches.forEach(addUrl);
  }

  return Array.from(urls);
};

const ToolResultDisplay: React.FC<{ content: string; name?: string }> = ({ content, name }) => {
  const [expanded, setExpanded] = useState(false);
  const isLong = content.length > TOOL_RESULT_PREVIEW_CHARS;
  const displayContent = expanded || !isLong ? content : content.substring(0, TOOL_RESULT_PREVIEW_CHARS);
  const audioUrls = extractAudioUrlsFromToolResult(content);

  return (
    <div>
      <div className="text-[8px] uppercase tracking-widest text-slate-600 mb-1 border-b border-[#2a2e39] pb-1 flex justify-between items-center">
        <span>
          <i className="fas fa-wrench mr-1"></i> Tool Result: {name}
          {isLong && <span className="text-slate-700 normal-case tracking-normal ml-2">({(content.length / 1024).toFixed(1)}KB)</span>}
        </span>
        {isLong && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-[8px] text-purple-400 hover:text-purple-300 font-bold normal-case tracking-normal"
          >
            {expanded ? (
              <><i className="fas fa-compress-alt mr-0.5"></i>Zwiń</>
            ) : (
              <><i className="fas fa-expand-alt mr-0.5"></i>Pokaż pełny ({(content.length).toLocaleString()} zn.)</>
            )}
          </button>
        )}
      </div>
      {audioUrls.length > 0 && (
        <div className="mb-2 space-y-2">
          {audioUrls.map((url, idx) => (
            <div key={`${url}-${idx}`} className="rounded border border-emerald-500/20 bg-emerald-500/5 p-2">
              <audio controls src={url} className="w-full h-8" preload="none" />
              <a
                href={url}
                download
                className="mt-1 inline-flex items-center gap-1 text-[8px] text-emerald-300 hover:text-emerald-200"
              >
                <i className="fas fa-download"></i>
                Pobierz audio {audioUrls.length > 1 ? idx + 1 : ''}
              </a>
            </div>
          ))}
        </div>
      )}
      <div className={`text-[8px] whitespace-pre-wrap ${expanded ? 'max-h-96 overflow-y-auto custom-scrollbar' : ''}`}>
        {displayContent}
        {!expanded && isLong && (
          <span className="text-purple-500/60 cursor-pointer" onClick={() => setExpanded(true)}>
            {'\n'}... [kliknij aby rozwinąć]
          </span>
        )}
      </div>
    </div>
  );
};

const ThinkingSessionPopup: React.FC<{
  visible: boolean;
  settings: ThinkingSettings;
  setSettings: React.Dispatch<React.SetStateAction<ThinkingSettings>>;
  steps: ThinkingStep[];
  running: boolean;
  onClose: () => void;
  onClear: () => void;
}> = ({ visible, settings, setSettings, steps, running, onClose, onClear }) => {
  if (!visible) return null;

  return (
    <div className="absolute inset-0 z-40 pointer-events-none">
      <div className="absolute top-3 right-3 w-[400px] max-w-[calc(100%-1.5rem)] bg-[#0d1117] border border-cyan-500/30 rounded shadow-2xl pointer-events-auto">
        <div className="px-3 py-2 border-b border-cyan-500/20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <i className={`fas ${running ? 'fa-brain fa-pulse' : 'fa-brain'} text-cyan-400`}></i>
            <span className="text-[10px] font-black uppercase tracking-widest text-cyan-300">Sequential Thinking</span>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-white text-xs">
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="p-3 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <label className="text-[8px] text-slate-400 uppercase tracking-wider">Sesje</label>
            <label className="text-[8px] text-slate-400 uppercase tracking-wider">Pamięć</label>
            <select
              value={settings.sessions}
              onChange={e => setSettings(prev => ({ ...prev, sessions: Math.min(8, Math.max(1, parseInt(e.target.value, 10) || 1)) }))}
              className="bg-[#131722] border border-[#2a2e39] rounded px-2 py-1 text-[9px] text-slate-200"
            >
              {[1, 2, 3, 4, 5, 6, 7, 8].map(v => <option key={v} value={v}>{v}</option>)}
            </select>
            <select
              value={settings.memoryMode}
              onChange={e => setSettings(prev => ({ ...prev, memoryMode: e.target.value as ThinkingMemoryMode }))}
              className="bg-[#131722] border border-[#2a2e39] rounded px-2 py-1 text-[9px] text-slate-200"
            >
              <option value="off">Off</option>
              <option value="compact">Compact</option>
              <option value="full">Full</option>
            </select>
          </div>

          <div className="flex items-center justify-between text-[8px] text-slate-400">
            <label className="flex items-center gap-1 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={settings.enabled}
                onChange={e => setSettings(prev => ({ ...prev, enabled: e.target.checked }))}
                className="w-3 h-3 accent-cyan-500"
              />
              Aktywne dla kolejnej wiadomości
            </label>
            <label className="flex items-center gap-1 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={settings.autoCloseOnResponse}
                onChange={e => setSettings(prev => ({ ...prev, autoCloseOnResponse: e.target.checked }))}
                className="w-3 h-3 accent-cyan-500"
              />
              Auto-zamknij
            </label>
          </div>

          <div className="border border-[#2a2e39] rounded bg-[#131722] p-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[8px] uppercase tracking-wider text-slate-500">Kroki myślenia</span>
              <button onClick={onClear} className="text-[8px] text-slate-500 hover:text-red-400">Wyczyść</button>
            </div>
            <div className="max-h-56 overflow-y-auto custom-scrollbar space-y-1">
              {steps.length === 0 && (
                <p className="text-[8px] text-slate-600 italic">Brak kroków. Wyślij wiadomość, aby rozpocząć sesję.</p>
              )}
              {steps.map(step => (
                <div key={step.id} className="border border-cyan-500/20 rounded p-1.5 bg-cyan-500/5">
                  <div className="flex justify-between text-[8px] text-cyan-300 mb-0.5">
                    <span>Sesja {step.session}</span>
                    <span>{new Date(step.timestamp).toLocaleTimeString('pl-PL')}</span>
                  </div>
                  <div className="text-[8px] text-slate-300 whitespace-pre-wrap">{step.summary}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AgentChat: React.FC<AgentChatProps> = ({ onToolCall, onConversationStateChange, addLog, auditMode = false }) => {
  const { sendMessage } = useAgentOrchestration();
  const [messages, setMessages] = useState<Message[]>([
    { role: 'assistant', content: 'OmniGate Architekt Neuronowy zainicjowany. Node-01 protokół konfiguracji w gotowości.' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [voiceMode, setVoiceMode] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [handsFreeMode, setHandsFreeMode] = useState(false);
  const [isInterrupting, setIsInterrupting] = useState(false);

  // ── Multimedia attachments ─────────────────────────────────────────────────
  const [pendingFiles, setPendingFiles] = useState<Attachment[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [videoFileToProcess, setVideoFileToProcess] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Phase 3: System prompt and character cards
  const [systemPrompt, setSystemPrompt] = useState(DEFAULT_SYSTEM_PROMPT);
  const [showPromptEditor, setShowPromptEditor] = useState(false);
  const [characterCards, setCharacterCards] = useState<CharacterCard[]>([]);
  const [activeCardId, setActiveCardId] = useState<string | null>(null);

  // New Feature: Neural Chat Endpoint Switcher
  const [availableModels, setAvailableModels] = useState<any[]>([]);
  const [selectedEndpoint, setSelectedEndpoint] = useState<string>('');
  const [selectedProvider, setSelectedProvider] = useState<string>('');
  const [selectedTTSModel, setSelectedTTSModel] = useState<string>(() => localStorage.getItem('omnigate_tts_model') || '');
  const [ttsSpeed, setTtsSpeed] = useState<number>(() => {
    const stored = localStorage.getItem('omnigate_tts_speed');
    return stored ? parseFloat(stored) : 1.0;
  });
  const [selectedASRModel, setSelectedASRModel] = useState<string>(() => localStorage.getItem('omnigate_asr_model') || '');

  // Vision & Audio model selectors
  const [selectedVisionModel, setSelectedVisionModel] = useState<string>(() =>
    localStorage.getItem('omnigate_vision_model') || 'microsoft/phi-4-multimodal-instruct'
  );

  // Synchronize selectedEndpoint with availableModels
  useEffect(() => {
    if (selectedEndpoint && !availableModels.some(m => m.id === selectedEndpoint)) {
      setSelectedEndpoint(availableModels.length > 0 ? availableModels[0].id : '');
    }
  }, [availableModels, selectedEndpoint]);

  // Synchronize model selectors with available models
  useEffect(() => {
    if (selectedVisionModel && !availableModels.some(m => m.id === selectedVisionModel)) {
      const visionModels = availableModels.filter(m => m.tags?.includes('vision'));
      setSelectedVisionModel(visionModels.length > 0 ? visionModels[0].id : 'microsoft/phi-4-multimodal-instruct');
    }
  }, [availableModels, selectedVisionModel]);

  useEffect(() => {
    if (selectedTTSModel && !availableModels.some(m => m.id === selectedTTSModel)) {
      setSelectedTTSModel('');
    }
  }, [availableModels, selectedTTSModel]);

  useEffect(() => {
    if (selectedASRModel && !availableModels.some(m => m.id === selectedASRModel)) {
      setSelectedASRModel('');
    }
  }, [availableModels, selectedASRModel]);
  const [reasoningMode, setReasoningMode] = useState<boolean>(() => localStorage.getItem('omnigate_reasoning') === 'true');
  const [toolCallingMode, setToolCallingMode] = useState<boolean>(() => localStorage.getItem('omnigate_tool_calling') !== 'false');


  // ── Translation state ──────────────────────────────────────────────────────
  const [sourceLang, setSourceLang] = useState<string>(() => localStorage.getItem('omnigate_src_lang') || 'auto');
  const [targetLang, setTargetLang] = useState<string>(() => localStorage.getItem('omnigate_tgt_lang') || 'en');
  const [translationStatus, setTranslationStatus] = useState<TranslationStatus>('idle');
  const [showTranslationPopup, setShowTranslationPopup] = useState(false);
  const [translationOriginal, setTranslationOriginal] = useState('');
  const [translationResult, setTranslationResult] = useState('');
  const [translationStreaming, setTranslationStreaming] = useState('');
  const [translationDetectedLang, setTranslationDetectedLang] = useState<string | undefined>();
  const [translationDurationMs, setTranslationDurationMs] = useState<number | undefined>();
  const [translationGlossaryApplied, setTranslationGlossaryApplied] = useState(false);
  const translationWsRef = useRef<WebSocket | null>(null);

  // Save lang preferences
  useEffect(() => { localStorage.setItem('omnigate_src_lang', sourceLang); }, [sourceLang]);
  useEffect(() => { localStorage.setItem('omnigate_tgt_lang', targetLang); }, [targetLang]);
  const NIM_VISION_OPTIONS = [
    { id: 'microsoft/phi-4-multimodal-instruct', label: 'phi-4-mm (2s)' },
    { id: 'meta/llama-3.2-11b-vision-instruct', label: 'llama-3.2-11b-v' },
    { id: 'nvidia/llama-3.1-nemotron-nano-vl-8b-v1', label: 'nemo-vl-8b' },
    { id: 'nvidia/nemotron-nano-12b-v2-vl', label: 'nemo-nano-12b-vl' },
    { id: 'meta/llama-4-scout-17b-16e-instruct', label: 'llama-4-scout' },
    { id: 'meta/llama-3.2-90b-vision-instruct', label: 'llama-3.2-90b-v' },
    { id: 'qwen/qwen3.5-122b-a10b', label: 'qwen3.5-122b (NIM)' },
    { id: 'moonshotai/kimi-k2.5', label: 'kimi-k2.5 (NIM)' },
    { id: 'MiniCPM-V-2_6-Q4_K_S', label: 'MiniCPM-V 2.6 (kobold)' },
    { id: 'gemma3:4b', label: 'gemma3:4b (local)' },
    { id: 'qwen2.5vl:3b', label: 'qwen2.5vl:3b (local)' },
  ];
  const [isTestingModels, setIsTestingModels] = useState(false);
  const [mcpEnabled, setMcpEnabled] = useState(true);

  // ── History & Context features ─────────────────────────────────────────────
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConvId, setCurrentConvId] = useState<string | null>(null);
  const [maxContextTokens, setMaxContextTokens] = useState<number>(() => {
    const saved = localStorage.getItem('omnigate_max_context_tokens');
    return saved ? parseInt(saved, 10) : 8000;
  });
  const [showContextSettings, setShowContextSettings] = useState(false);

  // ── Heuristic Auto-Correction State ────────────────────────────────────────
  const [autocorrectMode, setAutocorrectMode] = useState<boolean>(() => {
    return localStorage.getItem('omnigate_autocorrect') !== 'false';
  });
  const [heuristicPopupVisible, setHeuristicPopupVisible] = useState(false);
  const [heuristicBlocks, setHeuristicBlocks] = useState<CodeBlock[]>([]);

  // Analyze AI response for bad code blocks
  const analyzeResponseForCode = useCallback(async (responseText: string) => {
    if (!autocorrectMode || !responseText) return;
    try {
      const res = await fetch('/v1/heuristics/analyze-text', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ text: responseText })
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data.any_needs_repair && data.blocks?.length > 0) {
        setHeuristicBlocks(data.blocks.filter((b: CodeBlock) => b.needs_repair));
        setHeuristicPopupVisible(true);
        addLog?.('WARN', `Heuristic: wykryto ${data.blocks.filter((b: CodeBlock) => b.needs_repair).length} blok(ów) z problemami`, 'HeuristicEngine');
      }
    } catch (e) {
      // silent - don't interrupt chat
    }
  }, [autocorrectMode, addLog]);

  // Handle repair result — replace code in last assistant message
  const handleHeuristicRepair = useCallback((originalCode: string, repairedCode: string, _blockIdx: number) => {
    setMessages(prev => {
      const newMsgs = [...prev];
      // Find last assistant message and replace the code block
      for (let i = newMsgs.length - 1; i >= 0; i--) {
        if (newMsgs[i].role === 'assistant') {
          const escaped = originalCode.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const repaired = newMsgs[i].content
            .replace(escaped, repairedCode)
            .replace(new RegExp('```python?\n' + escaped + '```', 'sg'), '```python\n' + repairedCode + '```');
          newMsgs[i] = { ...newMsgs[i], content: repaired };
          break;
        }
      }
      return newMsgs;
    });
    setHeuristicPopupVisible(false);
    addLog?.('INFO', 'Heuristic: naprawiony kod wstawiony do wiadomości', 'HeuristicEngine');
  }, [addLog]);

  // Condensation state
  const [isCondensing, setIsCondensing] = useState(false);

  // Compute used context tokens from messages (uses contextContent when available)
  const usedContextTokens = useMemo(() => {
    return messages.reduce((sum, m) => sum + estimateMessageTokens(m), 0)
      + estimateTokens(systemPrompt);
  }, [messages, systemPrompt]);

  const contextPercent = Math.min(100, Math.round((usedContextTokens / maxContextTokens) * 100));
  const contextColor = contextPercent > 85 ? '#ef4444' : contextPercent > 60 ? '#f59e0b' : '#a855f7';

  const scrollRef = useRef<HTMLDivElement>(null);
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const audioChunks = useRef<Blob[]>([]);

  // Phase 1.3: Separate AudioContexts for playback and input
  const audioContextPlayback = useRef<AudioContext | null>(null);
  const audioContextInput = useRef<AudioContext | null>(null);

  const analyser = useRef<AnalyserNode | null>(null);
  const silenceTimer = useRef<NodeJS.Timeout | null>(null);
  const audioStream = useRef<MediaStream | null>(null);
  const audioWorker = useRef<Worker | null>(null);
  const adaptiveThreshold = useRef<number>(0.05);
  const ambientNoiseLevel = useRef<number>(0.02);
  const lastSpeechTime = useRef<number>(0);
  const ongoingPlayback = useRef<AudioBufferSourceNode | null>(null);

  // Phase 1.5: TTS Audio Queue
  const audioQueueParams = useRef<{ buffer: AudioBuffer; text: string }[]>([]);
  const isAudioQueuePlaying = useRef(false);

  // Phase 1.2: Synchronous recording guard
  const isRecordingRef = useRef(false);
  // Ref dla handsFreeMode — bez closure bug w callbackach async
  const handsFreeRef = useRef(false);

  // New: Session Interrupt Control
  const abortControllerRef = useRef<AbortController | null>(null);
  // ── ANTY-PĘTLA: session-scoped guard (nie gubi się przy shallow copy tablicy) ──
  const loopGuardRef = useRef<{
    successCalls: Set<string>;
    failCounts: Map<string, number>;
    toolFailCounts: Map<string, number>;
    iterationCount: number;
  }>({ successCalls: new Set(), failCounts: new Map(), toolFailCounts: new Map(), iterationCount: 0 });
  const [mcpServers, setMcpServers] = useState<string[]>([]);
  const [activeMcpServers, setActiveMcpServers] = useState<Set<string>>(new Set());
  const [showServerPicker, setShowServerPicker] = useState(false);
  const [showBrowserTaskPopup, setShowBrowserTaskPopup] = useState(true);

  // Sequential thinking console
  const [showThinkingPopup, setShowThinkingPopup] = useState(false);
  const [thinkingRunning, setThinkingRunning] = useState(false);
  const [thinkingSteps, setThinkingSteps] = useState<ThinkingStep[]>([]);
  const [thinkingSettings, setThinkingSettings] = useState<ThinkingSettings>(() => {
    try {
      const raw = localStorage.getItem('omnigate_thinking_settings');
      if (raw) {
        const parsed = JSON.parse(raw);
        return {
          enabled: parsed.enabled ?? true,
          sessions: Math.min(8, Math.max(1, parsed.sessions ?? 3)),
          memoryMode: parsed.memoryMode ?? 'compact',
          autoCloseOnResponse: parsed.autoCloseOnResponse ?? true,
        };
      }
    } catch {
      // ignore parse errors
    }
    return {
      enabled: true,
      sessions: 3,
      memoryMode: 'compact',
      autoCloseOnResponse: true,
    };
  });

  useEffect(() => {
    scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight);
  }, [messages]);

  // Load conversations from persistence
  const refreshConversations = useCallback(() => {
    setConversations(ChatPersistence.getAllConversations());
  }, []);

  // Load character cards, conversations, and fetch available models
  useEffect(() => {
    const saved = localStorage.getItem('omnigate_character_cards');
    if (saved) {
      try {
        const cards = JSON.parse(saved);
        setCharacterCards(cards);
      } catch (e) {
        console.error('Failed to load character cards:', e);
      }
    }

    // Init conversation persistence session
    const initConversation = () => {
      let convo = ChatPersistence.getCurrentConversation();
      if (!convo) {
        const title = `Sesja ${new Date().toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`;
        convo = ChatPersistence.createConversation(title);
      } else if (convo.messages && convo.messages.length > 0) {
        // Restore messages if existing convo was found and has messages
        setMessages(convo.messages.map(m => ({
          role: m.role as 'user' | 'assistant' | 'system',
          content: m.content,
          rawContent: m.content
        })));
      }
      setCurrentConvId(convo.id);
      refreshConversations();
    };

    const refreshModels = async () => {
      try {
        const service = aiService as any;
        const remoteModels = await service.getAvailableModels();
        const localModel = typeof service.getActiveConfigModel === 'function'
          ? service.getActiveConfigModel()
          : null;

        const merged = [...remoteModels];
        if (localModel && !merged.some((m: any) => m.id === localModel.id)) {
          merged.unshift(localModel);
        }

        setAvailableModels(merged);
        
        // Preserve selected endpoint if it's still in the available models
        // If not, default to the first available model or empty string
        setSelectedEndpoint(prev => {
          if (prev && merged.some((m: any) => m.id === prev)) {
            return prev;
          }
          return merged.length > 0 ? merged[0].id : '';
        });
      } catch (e) {
        console.error("Failed to refresh models:", e);
        // Fallback to local model if available
        try {
          const localModel = (aiService as any).getActiveConfigModel?.();
          if (localModel) {
            setAvailableModels([localModel]);
            setSelectedEndpoint(prev => prev || localModel.id);
          }
        } catch { }
      }
    };

    const loadMcpServers = async () => {
      const tools = await aiService.getMCPTools();
      const servers = Array.from(new Set(tools.map(t => t.server)));
      setMcpServers(servers);
      setActiveMcpServers(new Set(servers));
    };

    initConversation();
    refreshModels();
    loadMcpServers();

    // Subscribe to config changes
    const unsubscribe = (aiService as any).onConfigChange(() => {
      refreshModels();
    });

    return () => unsubscribe();
  }, [refreshConversations]);

  // Save max context to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('omnigate_max_context_tokens', maxContextTokens.toString());
  }, [maxContextTokens]);

  useEffect(() => {
    localStorage.setItem('omnigate_thinking_settings', JSON.stringify(thinkingSettings));
  }, [thinkingSettings]);

  // Continuously sync messages to current conversation in history
  useEffect(() => {
    if (currentConvId && messages.length > 0) {
      const all = ChatPersistence.getAllConversations();
      const current = all.find(c => c.id === currentConvId);

      // Only sync if messages changed significantly (avoid rapid re-renders)
      if (current && JSON.stringify(current.messages.map(m => m.content)) === JSON.stringify(messages.filter(m => m.role !== 'system' || m.isCondensed).map(m => m.content))) {
        return;
      }

      const updatedMsgs = messages
        .filter(m => m.role !== 'system' || m.isCondensed)
        .map((m, idx) => ({
          id: `msg-${currentConvId}-${idx}`, // Deterministic ID for context sync
          role: m.role as 'user' | 'assistant' | 'system',
          content: m.rawContent || m.content,
          timestamp: new Date().toISOString(),
        }));

      const patchedAll = all.map(c =>
        c.id === currentConvId
          ? { ...c, messages: updatedMsgs, updatedAt: new Date().toISOString() }
          : c
      );
      localStorage.setItem('omnigate_conversations', JSON.stringify(patchedAll));
      setConversations(patchedAll);
    }
  }, [messages, currentConvId]);

  const handleTestModels = async () => {
    setIsTestingModels(true);
    addLog?.('INFO', 'Initiating active model tests...', 'Architect');
    try {
      const service = aiService as any;
      const result = await service.testActiveModels();
      addLog?.('INFO', `Model tests completed: ${result.reports ? result.reports.length : 0} verified`, 'Architect');

      // Refresh models and merge with local config
      const remoteModels = await service.getAvailableModels();
      const localModel = typeof service.getActiveConfigModel === 'function'
        ? service.getActiveConfigModel()
        : null;

      const merged = localModel
        ? [localModel, ...remoteModels.filter((m: any) => m.id !== localModel.id)]
        : remoteModels;

      setAvailableModels(merged);
    } catch (e) {
      addLog?.('ERROR', 'Model testing failed', 'Architect');
    } finally {
      setIsTestingModels(false);
    }
  };

  const handleEndpointChange = (val: string) => {
    setSelectedEndpoint(val);
    aiService.setNeuralChatEndpoint(val || null);
    addLog?.('INFO', `Neural Chat endpoint switched to: ${val || 'OmniGate Local'}`, 'Architect');
  };

  const handleStop = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setLoading(false);
      addLog?.('WARN', 'Neural processing interrupted by operator.', 'Architect');
    }
  }, [addLog]);

  const toggleMcpServer = (server: string) => {
    const next = new Set(activeMcpServers);
    if (next.has(server)) next.delete(server);
    else next.add(server);
    setActiveMcpServers(next);
  };

  // ── handleTranslate — WebSocket streaming ───────────────────────────
  const handleTranslate = useCallback((text: string) => {
    if (!text.trim()) return;

    // Close any existing WS
    if (translationWsRef.current) {
      translationWsRef.current.close();
      translationWsRef.current = null;
    }

    setTranslationOriginal(text);
    setTranslationStreaming('');
    setTranslationResult('');
    setTranslationDetectedLang(undefined);
    setTranslationDurationMs(undefined);
    setTranslationGlossaryApplied(false);
    setTranslationStatus('detecting');
    setShowTranslationPopup(true);

    const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const wsHost = window.location.hostname;
    const wsPort = '1337'; // OmniGate backend port
    const wsUrl = `${wsProtocol}://${wsHost}:${wsPort}/v1/translate/stream`;

    const ws = new WebSocket(wsUrl);
    translationWsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({
        text,
        source_lang: sourceLang,
        target_lang: targetLang,
        apply_glossary: true,
      }));
    };

    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        switch (msg.type) {
          case 'detecting':
            setTranslationStatus('detecting');
            break;
          case 'detected':
            setTranslationDetectedLang(msg.lang);
            break;
          case 'start':
            setTranslationStatus('translating');
            break;
          case 'chunk':
            setTranslationStreaming(prev => prev + (msg.content || ''));
            break;
          case 'done':
            setTranslationResult(msg.translated || '');
            setTranslationDurationMs(msg.duration_ms);
            setTranslationGlossaryApplied(!!msg.glossary_applied);
            if (msg.detected_lang) setTranslationDetectedLang(msg.detected_lang);
            setTranslationStatus('done');
            ws.close();
            break;
          case 'error':
            setTranslationStatus('error');
            setTranslationResult(`Error: ${msg.message}`);
            ws.close();
            break;
        }
      } catch (err) {
        console.error('Translation WS parse error', err);
      }
    };

    ws.onerror = () => {
      setTranslationStatus('error');
      setTranslationResult('WebSocket connection failed.');
    };

    ws.onclose = () => {
      translationWsRef.current = null;
    };
  }, [sourceLang, targetLang]);

  const handleAddGlossary = useCallback(async (sourceWord: string, targetWord: string) => {
    try {
      await fetch('/v1/translate/glossary', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source_word: sourceWord, target_word: targetWord, source_lang: sourceLang, target_lang: targetLang }),
      });
      addLog?.('INFO', `Translation glossary: added "${sourceWord}" → "${targetWord}"`, 'Translator');
    } catch (e) {
      addLog?.('ERROR', `Glossary add failed: ${e}`, 'Translator');
    }
  }, [sourceLang, targetLang, addLog]);

  // Initialize Web Worker for audio processing
  useEffect(() => {
    if (typeof Worker !== 'undefined') {
      try {
        audioWorker.current = new Worker('/audio-worker.js');

        audioWorker.current.onmessage = (e) => {
          const { type, rms, threshold, ambientLevel } = e.data;

          switch (type) {
            case 'AUDIO_PROCESSED':
              if (rms !== undefined) {
                adaptiveThreshold.current = Math.max(0.02, rms * 1.5);
              }
              break;

            case 'AMBIENT_CALIBRATED':
              if (threshold !== undefined) {
                adaptiveThreshold.current = threshold;
                ambientNoiseLevel.current = ambientLevel;
              }
              break;
          }
        };
      } catch (e) {
        console.warn('Audio worker not available:', e);
      }
    }

    return () => {
      audioWorker.current?.terminate();
      audioContextPlayback.current?.close();
      audioContextInput.current?.close();
    };
  }, []);

  // Phase 1.4: Notify parent of conversation state changes
  useEffect(() => {
    const isConversationActive = isRecording || isSpeaking || loading;
    onConversationStateChange?.(isConversationActive);
  }, [isRecording, isSpeaking, loading, onConversationStateChange]);

  // Phase 1.3: Separate audio context for playback
  const ensurePlaybackContext = async () => {
    if (!audioContextPlayback.current) {
      audioContextPlayback.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    // Only resume if not closed and we have user interaction
    if (audioContextPlayback.current.state === 'suspended') {
      try {
        await audioContextPlayback.current.resume();
      } catch (error) {
        console.warn('Failed to resume audio context:', error);
        // Create new context if resume fails
        audioContextPlayback.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
    }
    return audioContextPlayback.current;
  };

  // Phase 1.3: Separate audio context for input/recording
  const ensureInputContext = async () => {
    if (!audioContextInput.current) {
      audioContextInput.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    // Only resume if not closed and we have user interaction
    if (audioContextInput.current.state === 'suspended') {
      try {
        await audioContextInput.current.resume();
      } catch (error) {
        console.warn('Failed to resume input audio context:', error);
        // Create new context if resume fails
        audioContextInput.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
    }
    return audioContextInput.current;
  };

  // Stop any ongoing playback for interruption
  const stopOngoingPlayback = () => {
    audioQueueParams.current = []; // Clear queue on interruption
    isAudioQueuePlaying.current = false;
    if (ongoingPlayback.current) {
      try {
        ongoingPlayback.current.stop();
      } catch (e) {
        // May already be stopped
      }
      ongoingPlayback.current = null;
      setIsSpeaking(false);
    }
  };

  const playNextInQueue = async (ctx: AudioContext) => {
    if (isAudioQueuePlaying.current || audioQueueParams.current.length === 0) return;

    isAudioQueuePlaying.current = true;
    const { buffer, text } = audioQueueParams.current.shift()!;

    setIsSpeaking(true);

    const source = ctx.createBufferSource();
    ongoingPlayback.current = source;
    source.buffer = buffer;

    // Add GainNode for volume control
    const gainNode = ctx.createGain();
    gainNode.gain.value = 0.9;
    source.connect(gainNode);
    gainNode.connect(ctx.destination);

    source.onended = () => {
      ongoingPlayback.current = null;
      isAudioQueuePlaying.current = false;

      if (audioQueueParams.current.length > 0) {
        playNextInQueue(ctx);
      } else {
        setIsSpeaking(false);
        addLog?.('INFO', 'Speech playback completed', 'Architect');

        // Użyj handsFreeRef — unika closure bug (handsFreeMode byłoby stale)
        if (handsFreeRef.current && !isRecordingRef.current && !isInterrupting) {
          setTimeout(() => {
            if (!isRecordingRef.current) startRecording();
          }, 400);
        }
      }
    };

    source.start();
  };

  const playResponse = async (text: string) => {
    if (!voiceMode) return;

    // Obetnij długie odpowiedzi dla TTS — max 400 znaków, uciąj na końcu zdania
    let ttsText = text
      .replace(/<[^>]+>/g, '')           // usuń HTML tagi
      .replace(/```[s\S]*?```/g, '')    // usuń bloki kodu
      .replace(/\*\*|\*|__|_|#{1,6} /g, '') // usuń markdown
      .trim();

    if (ttsText.length > 400) {
      // Utnij na końcu ostatniego zdania przed limitem
      const cutoff = ttsText.lastIndexOf('.', 400);
      ttsText = cutoff > 100 ? ttsText.slice(0, cutoff + 1) : ttsText.slice(0, 400);
    }

    if (!ttsText) return;

    try {
      setIsInterrupting(false);
      addLog?.('INFO', `Synthesizing queued response (${ttsText.length}/${text.length} chars)`, 'Architect');

      // Phase 1.3: Use separate playback context
      const ctx = await ensurePlaybackContext();

      // Check if context is usable
      if (ctx.state === 'closed') {
        console.warn('Audio context is closed, creating new one');
        audioContextPlayback.current = new (window.AudioContext || (window as any).webkitAudioContext)();
        await audioContextPlayback.current.resume();
      }

      const audioData = await aiService.synthesizeSpeech(ttsText, 'pl', selectedTTSModel, ttsSpeed);

      if (audioData) {
        const buffer = await ctx.decodeAudioData(audioData);
        audioQueueParams.current.push({ buffer, text: ttsText });
        playNextInQueue(ctx);
      } else {
        addLog?.('WARN', 'TTS synthesis returned no audio data', 'Architect');
      }
    } catch (e) {
      console.error("TTS Playback failed", e);
      addLog?.('ERROR', `TTS playback failed: ${e instanceof Error ? e.message : 'Unknown error'}`, 'Architect');
    }
  };

  const startRecording = async () => {
    // Phase 1.2: Use ref for immediate synchronous guard
    if (isRecordingRef.current || (isSpeaking && !handsFreeMode)) return;

    // Stop ongoing playback if in hands-free mode and interrupting
    if (isSpeaking && handsFreeMode) {
      setIsInterrupting(true);
      stopOngoingPlayback();
    }

    // Phase 1.2: Set ref immediately before any async operations
    isRecordingRef.current = true;
    setIsRecording(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStream.current = stream;

      // Phase 1.2: Validate MediaRecorder can be created
      if (!MediaRecorder.isTypeSupported('audio/webm')) {
        mediaRecorder.current = new MediaRecorder(stream);
      } else {
        mediaRecorder.current = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      }

      audioChunks.current = [];

      if (handsFreeMode) {
        // Phase 1.3: Use separate input context for analysis
        const ctx = await ensureInputContext();
        analyser.current = ctx.createAnalyser();
        const source = ctx.createMediaStreamSource(stream);
        source.connect(analyser.current);
        analyser.current.fftSize = 2048;
        monitorSilenceWithAdaptiveThreshold();
      }

      mediaRecorder.current.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunks.current.push(event.data);
      };

      mediaRecorder.current.onstop = async () => {
        if (silenceTimer.current) {
          clearInterval(silenceTimer.current);
          silenceTimer.current = null;
        }

        const audioBlob = new Blob(audioChunks.current, { type: 'audio/wav' });

        // Phase 1.6: Hands-Free VAD loop fix for empty recordings
        if (handsFreeMode && audioBlob.size < 5000) {
          audioStream.current?.getTracks().forEach(t => t.stop());
          audioStream.current = null;
          isRecordingRef.current = false;
          setIsRecording(false);
          setTimeout(() => {
            if (!isRecordingRef.current && handsFreeRef.current) startRecording();
          }, 300);
          return;
        }

        setLoading(true);

        try {
          const text = await aiService.transcribeAudio(audioBlob, selectedASRModel);
          if (text && text.trim().length > 0) {
            addLog?.('INFO', `Voice transcribed: ${text.substring(0, 50)}${text.length > 50 ? '...' : ''}`, 'Architect');
            if (handsFreeMode) {
              setMessages(prev => [...prev, { role: 'user', content: text }]);
              await sendToAgent(text);
            } else {
              setInput(prev => prev + (prev ? ' ' : '') + text);
            }
          } else {
            addLog?.('WARN', 'Voice transcription returned empty result', 'Architect');
            // VAD loop fix: restart on empty transcript
            if (handsFreeMode) {
              setTimeout(() => {
                if (!isRecordingRef.current && handsFreeRef.current) startRecording();
              }, 300);
            }
          }
        } catch (e) {
          console.error("Transcription failed", e);
          addLog?.('ERROR', `Voice transcription failed: ${e instanceof Error ? e.message : 'Unknown error'}`, 'Architect');
          // VAD loop fix: restart on error if hands-free is enabled
          if (handsFreeMode) {
            setTimeout(() => {
              if (!isRecordingRef.current && handsFreeRef.current) startRecording();
            }, 1000);
          }
        } finally {
          setLoading(false);
          audioStream.current?.getTracks().forEach(t => t.stop());
          audioStream.current = null;
          // Phase 1.2: Clear ref only after full cleanup
          isRecordingRef.current = false;
          // Notice: We don't call setIsRecording(false) here if we're going to restart, but safety sync:
          if (!handsFreeMode) setIsRecording(false);
        }
      };

      // Phase 1.2: Validate MediaRecorder state before start
      if (mediaRecorder.current.state === 'inactive') {
        mediaRecorder.current.start();
      }

    } catch (e) {
      console.error("Mic access denied", e);
      addLog?.('ERROR', `Microphone access denied: ${e instanceof Error ? e.message : 'Permission denied'}`, 'Architect');
      // Phase 1.2: Reset refs on error
      isRecordingRef.current = false;
      setIsRecording(false);
    }
  };

  const stopRecording = () => {
    // Phase 1.2: Validate MediaRecorder state before stop
    if (mediaRecorder.current && mediaRecorder.current.state === 'recording') {
      mediaRecorder.current.stop();
    }
    setIsRecording(false);
    // Note: isRecordingRef will be cleared in onstop handler after cleanup
  };

  // Monitor silence with adaptive threshold
  const monitorSilenceWithAdaptiveThreshold = () => {
    if (!analyser.current) return;

    const bufferLength = analyser.current.fftSize;
    const dataArray = new Uint8Array(bufferLength);
    let silenceStart = Date.now();

    // Phase 1.2: Increase polling from 20ms to 100ms for stability
    const CHECK_INTERVAL = 100;
    const MIN_SILENCE_DURATION = 800;

    const checkVolume = async () => {
      // Phase 1.2: Validate MediaRecorder state
      if (!analyser.current || !mediaRecorder.current || mediaRecorder.current.state !== 'recording') {
        if (silenceTimer.current) {
          clearInterval(silenceTimer.current);
          silenceTimer.current = null;
        }
        return;
      }

      analyser.current.getByteTimeDomainData(dataArray);

      // Send audio data to Web Worker for processing
      if (audioWorker.current) {
        audioWorker.current.postMessage({
          type: 'PROCESS_AUDIO',
          data: { audioBuffer: Array.from(dataArray), chunk: Date.now() },
          config: { threshold: adaptiveThreshold.current }
        });
      }

      // Calculate RMS locally as backup
      let sum = 0;
      for (let i = 0; i < bufferLength; i++) {
        const normalized = (dataArray[i] - 128) / 128;
        sum += normalized * normalized;
      }
      const rms = Math.sqrt(sum / bufferLength);

      // Use adaptive threshold
      if (rms < adaptiveThreshold.current) {
        if (Date.now() - silenceStart > MIN_SILENCE_DURATION) {
          stopRecording();
        }
      } else {
        silenceStart = Date.now();
      }
    };

    // Clear any existing timer
    if (silenceTimer.current) {
      clearInterval(silenceTimer.current);
    }

    silenceTimer.current = setInterval(checkVolume, CHECK_INTERVAL);
  };

  const toggleRecording = () => {
    if (isRecordingRef.current || isRecording) {
      stopRecording();
      addLog?.('INFO', 'Voice recording stopped', 'Architect');
    } else {
      startRecording();
      addLog?.('INFO', 'Voice recording started', 'Architect');
    }
  };

  // Auto-start nagrywania gdy głośnomówiący zostaje włączony
  useEffect(() => {
    handsFreeRef.current = handsFreeMode;
    if (handsFreeMode && !isRecordingRef.current && !isSpeaking) {
      setTimeout(() => startRecording(), 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handsFreeMode]);

  // ── File → Attachment helper ───────────────────────────────────────────────
  const processFile = (file: File): Promise<Attachment> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const mimeType = file.type || 'application/octet-stream';
      let type: Attachment['type'] = 'file';
      if (mimeType.startsWith('image/')) type = 'image';
      else if (mimeType.startsWith('audio/')) type = 'audio';
      else if (mimeType.startsWith('video/')) type = 'video';
      else if (mimeType === 'application/pdf') type = 'pdf';
      else if (mimeType.startsWith('text/')) type = 'text';
      resolve({ id: `att-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, name: file.name, type, mimeType, dataUrl, size: file.size });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleFileDrop = async (files: FileList | File[]) => {
    const arr = Array.from(files);
    const videos = arr.filter(f => f.type.startsWith('video/'));
    const others = arr.filter(f => !f.type.startsWith('video/'));

    if (videos.length > 0) {
      // Jeśli jest wideo, przechwyć pierwsze do panelu VLM
      setVideoFileToProcess(videos[0]);
    }

    if (others.length > 0) {
      const processed = await Promise.all(others.map(processFile));
      setPendingFiles(prev => [...prev, ...processed]);
    }
  };

  const removePendingFile = (id: string) => setPendingFiles(prev => prev.filter(f => f.id !== id));

  const runSequentialThinking = useCallback(async (userMsg: string): Promise<ThinkingStep[]> => {
    if (!thinkingSettings.enabled || !userMsg.trim()) return [];
    const sessions = Math.min(8, Math.max(1, thinkingSettings.sessions));
    const steps: ThinkingStep[] = [];
    setThinkingRunning(true);

    for (let idx = 0; idx < sessions; idx++) {
      const thoughtBase = idx === 0
        ? `Zadanie operatora: ${userMsg}`
        : `Kontynuacja analizy. Poprzedni krok: ${steps[steps.length - 1]?.summary || 'brak'}`;

      const args = {
        thought: thoughtBase,
        thoughtNumber: idx + 1,
        totalThoughts: sessions,
        nextThoughtNeeded: idx < sessions - 1,
      };

      const executeWithName = async (toolName: string) => {
        const result = await aiService.executeMCPTool(toolName, args);
        if (typeof result === 'string') return result;
        return result?.thought || result?.summary || JSON.stringify(result);
      };

      let summary = '';
      let source: ThinkingStep['source'] = 'sequentialthinking';
      try {
        summary = await executeWithName('sequentialthinking');
      } catch {
        try {
          summary = await executeWithName('sequential-thinking');
        } catch {
          source = 'fallback';
          summary = `${thoughtBase}\n(awaryjnie: lokalna sesja myślenia bez narzędzia MCP)`;
        }
      }

      const step: ThinkingStep = {
        id: `think-${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 6)}`,
        session: idx + 1,
        thought: thoughtBase,
        summary,
        timestamp: Date.now(),
        source,
      };
      steps.push(step);
      setThinkingSteps(prev => [...prev, step]);
    }

    setThinkingRunning(false);
    return steps;
  }, [thinkingSettings.enabled, thinkingSettings.sessions]);

  const buildThinkingMemory = useCallback((steps: ThinkingStep[]) => {
    if (!steps.length || thinkingSettings.memoryMode === 'off') return '';
    if (thinkingSettings.memoryMode === 'full') {
      return steps.map(s => `Krok ${s.session}: ${s.summary}`).join('\n');
    }
    return steps.map(s => `K${s.session}: ${s.summary.slice(0, 240)}`).join('\n');
  }, [thinkingSettings.memoryMode]);

  const sendToAgent = async (userMsg: string, attachments: Attachment[] = [], existingHistory: any[] = null) => {
    if (!existingHistory) setLoading(true);
    const startTime = Date.now();
    const isTopLevelTurn = !existingHistory && !!userMsg.trim();

    // Reset session-scoped loop guards dla nowej tury (nie dla iteracji agenta)
    if (isTopLevelTurn) {
      loopGuardRef.current = { successCalls: new Set(), failCounts: new Map(), toolFailCounts: new Map(), iterationCount: 0 };
    }

    loopGuardRef.current.iterationCount += 1;
    if (loopGuardRef.current.iterationCount > 25) {
      addLog?.('ERROR', 'Max global iteration steps reached (25). Emergency brake deployed.', 'Architect');
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: '⚠️ Przepraszam, wyczerpałem limit operacji w tym zadaniu. Proszę przeformułować polecenie.',
        timestamp: Date.now(),
      }]);
      setLoading(false);
      return;
    }

    if (!existingHistory && userMsg) {
      addLog?.('AI_ACTION', `User: ${userMsg}`, 'Architect');
    }

    // Session ID — persisted in localStorage for continuity
    const sessionId = localStorage.getItem('omnigate_session_id') || `session-${Date.now()}`;
    if (!localStorage.getItem('omnigate_session_id')) {
      localStorage.setItem('omnigate_session_id', sessionId);
    }

    try {
      let fullHistory = existingHistory;
      let precomputedSteps: ThinkingStep[] = [];
      if (isTopLevelTurn && thinkingSettings.enabled) {
        setShowThinkingPopup(true);
        addLog?.('INFO', `Sequential Thinking: start (${thinkingSettings.sessions} sesji)`, 'Architect');
        precomputedSteps = await runSequentialThinking(userMsg);
      }

      if (!fullHistory) {
        const history = messages.map(m => {
          // Prefer contextContent (compressed) over rawContent/content for context efficiency
          let content = m.contextContent || m.rawContent || m.content;

          // Condensed summary messages → send as 'assistant' so model treats as prior context
          if (m.isCondensed) {
            return {
              role: 'assistant' as const,
              content: `[PODSUMOWANIE DOTYCHCZASOWEJ ROZMOWY]:\n${content}`,
              toolCalls: undefined,
              toolCallId: undefined,
              name: undefined
            };
          }

          // Format tool results clearly for context
          if (m.role === 'tool') {
            let safeContent = m.contextContent || content;
            if (safeContent.length > MAX_TOOL_RESULT_CONTEXT_CHARS) {
              safeContent = safeContent.substring(0, MAX_TOOL_RESULT_CONTEXT_CHARS) + '\n[...skrócono]';
            }
            // Clean, brief format instead of ### headers that models echo
            content = `[Wynik narzędzia ${m.name || ''}]: ${safeContent}`;
          }

          let contentToPass: any = content;
          if (m.attachments && m.attachments.length > 0) {
            const hasImages = m.attachments.some(a => a.type === 'image');
            if (hasImages) {
              contentToPass = [{ type: "text", text: typeof content === 'string' ? content : "" }];
              m.attachments.forEach(a => {
                if (a.type === 'image') {
                  contentToPass.push({ type: "image_url", image_url: { url: a.dataUrl } });
                }
              });
            }
          }

          // Sanitize history for Ollama: strip orphan tool_calls / tool_call_id.
          // Ollama 400s if history contains assistant.tool_calls without a matching 'tools' field,
          // or role:'tool' + tool_call_id on non-native-tool models.
          // Both are cleaned here so every turn starts from a safe baseline.
          const sanitizedRole = m.role === 'system' ? 'user' : m.role === 'tool' ? 'user' : m.role;
          return {
            role: sanitizedRole,
            content: contentToPass,
            // Strip toolCalls from assistant history — orphan tool_calls cause HTTP 400
            toolCalls: undefined,
            // Strip toolCallId — not needed when role is already converted to 'user'
            toolCallId: undefined,
            name: undefined
          };
        });

        const mcpContext = activeMcpServers.has('blender')
          ? "\n[ZASADA SPECJALNA]: Wykryto serwer BLENDER. Do wszystkich skryptów graficznych/manipulacji 3D ZAWSZE używaj narzędzia `execute_blender_code`. Nigdy nie używaj shella do pythona w tym celu."
          : "";

        const thinkingMemory = buildThinkingMemory(precomputedSteps);
        const thinkingContext = thinkingMemory
          ? `\n[SEQUENTIAL THINKING MEMORY]\n${thinkingMemory}\n[END SEQUENTIAL THINKING MEMORY]\n`
          : '';

        fullHistory = [
          { role: 'user', content: `[SYSTEM PROMPT]\n${systemPrompt}${mcpContext}\n${thinkingContext}[END SYSTEM PROMPT]` },
          { role: 'assistant', content: 'Rozumiem. Jestem gotowy do pomocy i będę używać wskazanych narzędzi MCP.' },
          ...history
        ];
      }

      // 🖼️ Enrich last message with attachment context for VLM
      const attachmentContext = attachments.length > 0
        ? `\n\n[ZAŁĄCZNIKI: ${attachments.map(a => `${a.name} (${a.type})`).join(', ')}]`
        : '';

      const finalPrompt = userMsg ? userMsg + attachmentContext : "";

      // Initialize AbortController for this turn
      abortControllerRef.current = new AbortController();

      const result = await aiService.runAgentSession(
        finalPrompt,
        fullHistory,
        mcpEnabled,
        abortControllerRef.current.signal,
        Array.from(activeMcpServers),
        maxContextTokens
      );

      const assistantMsg: Message = {
        role: 'assistant',
        content: result.text || "",
        rawContent: result.rawText,
        toolCalls: result.toolCalls?.map((tc: any) => tc.raw)
      };

      setMessages(prev => [...prev, assistantMsg]);

      const nextHistory = [...fullHistory];
      if (finalPrompt) {
        nextHistory.push({ role: 'user', content: finalPrompt });
      }
      nextHistory.push({
        role: 'assistant',
        content: assistantMsg.content,
        toolCalls: assistantMsg.toolCalls
      });

      if (result.text) {
        // limit text to 100 for console logging
        addLog?.('AI_ACTION', `Architect: ${result.text.substring(0, 100)}${result.text.length > 100 ? '...' : ''}`, 'Architect');
        playResponse(result.text);

        // 🔍 Heuristic Auto-Correction: analyze response for bad code
        analyzeResponseForCode(result.text);

        if (isTopLevelTurn && thinkingSettings.autoCloseOnResponse) {
          setShowThinkingPopup(false);
        }

        // 💾 Save to history API (fire-and-forget, don't block chat)
        const apiKey = localStorage.getItem('omnigate_api_key') || '';
        if (apiKey && userMsg) {
          fetch('/v1/history/chat', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${apiKey}`,
              'X-API-Key': apiKey,
            },
            body: JSON.stringify({
              session_id: sessionId,
              user_message: userMsg,
              assistant_response: result.text,
              model: localStorage.getItem('omni_model_chat') || 'qwen3-vl:4b',
              latency_ms: Date.now() - startTime,
            })
          }).catch(() => { }); // silent fail
        }
      }

      if (result.toolCalls && result.toolCalls.length > 0) {
        for (const call of result.toolCalls) {
          // ── ABORT GUARD: sprawdź czy użytkownik kliknął STOP ─────────────
          if (abortControllerRef.current?.signal.aborted) {
            addLog?.('WARN', 'Agent loop aborted by user (tool call loop)', 'Architect');
            return;
          }

          // Ignoruj klucz `_raw` wypychany do fallbacku ReAct'a - unikalne znaki formatowania nie powinny zmieniać sygnatury narzędzia.
          const cleanArgs = { ...call.functionCall.args };
          delete cleanArgs._raw;
          const callSig = `${call.functionCall.name}::${JSON.stringify(cleanArgs)}`;

          // ── SUKCES-GUARD: blokuj identyczną komendę która już się udała (loopGuardRef) ──
          if (loopGuardRef.current.successCalls.has(callSig)) {
            addLog?.('WARN', `Sukces-guard: ${call.functionCall.name} już wykonano pomyślnie — pomijam powtórzenie`, 'Architect');
            nextHistory.push({
              role: 'user',
              content: `[ANTY-PĘTLA] Narzędzie ${call.functionCall.name} z identycznymi argumentami zostało już pomyślnie wykonane. Zadanie jest UKOŃCZONE. Podsumuj co zostało zrobione i zakończ.`
            });
            await sendToAgent("", [], nextHistory);
            return;
          }

          // ── BŁĄD-GUARD: blokuj identyczny błąd po raz drugi (loopGuardRef) ──
          const failCount = loopGuardRef.current.failCounts.get(callSig) || 0;
          if (failCount >= 1) {
            addLog?.('ERROR', `Błąd-guard: ${call.functionCall.name} zablokowany po ${failCount} błędach (identyczne arg)`, 'Architect');
            nextHistory.push({
              role: 'user',
              content: `[STOP PĘTLI] Narzędzie ${call.functionCall.name} zwróciło ten sam błąd przy identycznych argumentach. Nie wywołuj go ponownie. Sprawdź dostępne narzędzia przed kolejnym ruchem.`
            });
            await sendToAgent("", [], nextHistory);
            return;
          }

          // ── HARD BŁĄD-GUARD: po 3 błędach danego narzędzia z RZĘDU blokuj ──
          const consecutiveToolFailures = loopGuardRef.current.toolFailCounts.get(call.functionCall.name) || 0;
          if (consecutiveToolFailures >= 3) {
            addLog?.('ERROR', `Hard błąd-guard: ${call.functionCall.name} zablokowany po 3 różnych błędnych próbach.`, 'Architect');
            nextHistory.push({
              role: 'user',
              content: `[HARD STOP] Próbowałeś użyć narzędzia ${call.functionCall.name} 3 razy i za każdym razem zwróciło ono błąd (nawet ze zmienionymi argumentami). Narzędzie to ewidentnie nie działa w obecnym środowisku lub brakuje wymaganego parametru. ZABRANIA SIĘ ponownego użycia tego narzędzia w tej turze. Sprawdź czy inne narzędzie może pomóc, a jeśli nie - ZAKOŃCZ odpowiedź bez narzędzi opublikowując wniosek dlaczego się to nie udało.`
            });
            await sendToAgent("", [], nextHistory);
            return;
          }

          // open_app loop-guard — używa loopGuardRef dla persystencji między rekurencjami
          if (call.functionCall.name === 'open_app') {
            const openAppCount = (loopGuardRef.current.failCounts.get('__open_app_consecutive') || 0) + 1;
            loopGuardRef.current.failCounts.set('__open_app_consecutive', openAppCount);
            if (openAppCount >= 2) {
              addLog?.('WARN', `open_app loop-guard: called ${openAppCount}x in a row — injecting instruction`, 'Architect');
              nextHistory.push({
                role: 'user',
                content: `[LOOP GUARD] open_app() zostało wywołane ${openAppCount} razy z rzędu. STOP. Telegram jest już otwarty. ` +
                  `Sprawdź ostatni screenshot — jeśli widzisz "Napisz wiadomość" lub "wrun" → zrób TYLKO: ` +
                  `tap(x=360, y=450) aby aktywować pole tekstowe, następnie type_text(text="/models"). ` +
                  `NIE wywołuj open_app() ponownie. Działaj na podstawie widocznego ekranu.`
              });
              await sendToAgent("", [], nextHistory);
              return;
            }
          } else if (['tap', 'type_text', 'swipe'].includes(call.functionCall.name)) {
            // reset open_app counter when model does something useful
            loopGuardRef.current.failCounts.set('__open_app_consecutive', 0);
          }

          onToolCall(call.functionCall);
          addLog?.('INFO', `Executing MCP tool: ${call.functionCall.name}`, 'Architect');

          let toolContent = "";
          let isError = false;
          let errorMessage = "";
          try {
            // Inject vision model for nexus-vision tools
            const VISION_TOOLS = ['vision_describe_screen', 'vision_analyze_base64', 'vision_analyze_url'];
            const toolArgs = VISION_TOOLS.includes(call.functionCall.name)
              ? { ...call.functionCall.args, model: selectedVisionModel }
              : call.functionCall.args;
            const toolResult = await aiService.executeMCPTool(call.functionCall.name, toolArgs);
            toolContent = typeof toolResult === 'string' ? toolResult : JSON.stringify(toolResult);
            addLog?.('INFO', `MCP tool result: ${toolContent.substring(0, 100)}...`, 'Architect');
          } catch (e: any) {
            isError = true;
            errorMessage = e.message || 'unknown error';
            toolContent = `Błąd narzędzia ${call.functionCall.name}: ${errorMessage}`;
            addLog?.('ERROR', `MCP tool failed: ${call.functionCall.name} - ${errorMessage}`, 'Architect');
          }

          // ── Android Screenshot: VLM auto-analysis ─────────────────────
          // qwen3-coder:8b is text-only — strip base64 ALWAYS, replace with VLM description.
          // Even if VLM fails, contextVersion must NEVER contain raw base64.
          let screenshotVlmDescription = "";
          let screenshotMeta = "";

          if (!isError && call.functionCall.name === 'screenshot') {
            // Build base64-free metadata block (always done, VLM or not)
            try {
              const p = JSON.parse(toolContent);
              const w = p.image_width || 720;
              const h = p.image_height || 1480;
              screenshotMeta = `screenshot_ok: ${w}x${h}px, format=${p.format || 'jpeg'}\n` +
                `tap_coords: X:0-${w} Y:0-${h} — tap(x=X,y=Y) uses exact pixels`;
            } catch { screenshotMeta = 'screenshot_ok: 720x1480px'; }

            try {
              let parsed: any = {};
              try { parsed = JSON.parse(toolContent); } catch { }
              const b64 = parsed?.image_base64;
              if (b64 && b64.length > 100) {
                const mimeType = parsed?.format === 'jpeg' ? 'image/jpeg' : 'image/png';
                const w = parsed?.image_width || 720;
                const h = parsed?.image_height || 1480;
                addLog?.('INFO', `Screenshot ${w}x${h} — calling VLM...`, 'Architect');
                // fastMode=true: 20s timeout, 1200 tokens (VLM cold start ~2-3s on this GPU)
                const vlmPrompt =
                  `Android screen ${w}x${h}px. Describe what is on screen. ` +
                  `IMPORTANT: DO NOT list individual keyboard keys (Q,W,E,R,T,Y etc). Just say "keyboard visible" if keyboard is shown. ` +
                  `FOCUS ON: app name, screen/state name, chat messages, usernames, buttons, input fields, navigation elements. ` +
                  `List max 25 most important UI elements with coords. Format: "NAME at (X,Y)". ` +
                  `If chat input field visible: write "Input field 'Napisz wiadomość' at (X,Y)" with EXACT coords. ` +
                  `If Telegram chat open: write "Telegram chat with USER_NAME open" and input field coords. ` +
                  `X range: 0-${w}, Y range: 0-${h}. Be precise. Stop after 25 elements.`;
                const raw = await aiService.analyzeVision(b64, mimeType, vlmPrompt, true, selectedVisionModel);
                // Accept any non-empty result — even if it starts with <think> it was stripped by backend
                screenshotVlmDescription = (raw || '').trim();
                if (screenshotVlmDescription) {
                  addLog?.('INFO', `VLM OK: ${screenshotVlmDescription.substring(0, 120)}...`, 'Architect');
                } else {
                  addLog?.('WARN', 'VLM returned empty — using metadata fallback', 'Architect');
                  screenshotVlmDescription = `[VLM returned empty] ${screenshotMeta}`;
                }
              }
            } catch (vlmErr: any) {
              addLog?.('WARN', `VLM failed: ${vlmErr.message}`, 'Architect');
              screenshotVlmDescription = `[VLM error: ${vlmErr.message}] ${screenshotMeta}`;
            }
          }
          // ──────────────────────────────────────────────────────────────

          // Store full content for display, compressed for context.
          // For screenshots: ALWAYS build base64-free contextVersion.
          let contextVersion: string;
          if (call.functionCall.name === 'screenshot' && !isError) {
            // Never send base64 to the text model — always use VLM description (or metadata fallback)
            const description = screenshotVlmDescription || `[VLM unavailable] ${screenshotMeta}`;
            contextVersion = `[SCREENSHOT ANALYSIS]\n${screenshotMeta}\nSCREEN CONTENT:\n${description}`;
          } else {
            contextVersion = toolContent.length > MAX_TOOL_RESULT_CONTEXT_CHARS
              ? toolContent.substring(0, MAX_TOOL_RESULT_CONTEXT_CHARS) + '\n[...skrócono]'
              : toolContent;
          }

          const toolMsg: Message = {
            role: 'tool',
            // For screenshots: show VLM description in UI too (not raw base64)
            content: (call.functionCall.name === 'screenshot' && !isError) ? contextVersion : toolContent,
            contextContent: contextVersion,
            toolCallId: call.id || call.raw?.id,
            name: call.functionCall.name
          };

          setMessages(prev => [...prev, toolMsg]);

          // On error: fetch skill-based positive context from heuristic engine
          let skillHint = "";
          if (isError) {
            try {
              const hintRes = await fetch('/v1/heuristics/tool-error', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ tool_name: call.functionCall.name, error_message: errorMessage })
              });
              if (hintRes.ok) {
                const hintData = await hintRes.json();
                const skillName = hintData.skill || 'general';
                const fix = hintData.fix_suggestion || '';
                const positive = hintData.positive_pattern || '';
                const example = hintData.example || '';
                skillHint = `\n\n[SKILL HINT — ${skillName}]\nNAPRAWA: ${fix}\nWZORZEC POZYTYWNY: ${positive}` + (example ? `\nPRZYKŁAD: ${example}` : '');
                addLog?.('INFO', `HeuristicEngine: skill hint dla ${call.functionCall.name} → ${skillName}`, 'Architect');
              }
            } catch (_) { /* silent — nie blokuj odp */ }
          }

          // Use compressed content for context history to save tokens
          // WAŻNE: NIE dodawaj [ZADANIE]/[KONTYNUUJ] tutaj — model interpretuje
          // ten tekst dosłownie i wywołuje tts_speak z treścią instrukcji!
          const contextToolContent = isError
            ? `[BŁĄD NARZĘDZIA ${call.functionCall.name}]: ${contextVersion}${skillHint}\n\nJeśli zadanie nie jest ukończone, natychmiast wywołaj alternatywne narzędzie przez <tool_call>.`
            : `[Wynik narzędzia ${call.functionCall.name}]: ${contextVersion}\n\nJeśli zadanie nie jest jeszcze ukończone, natychmiast wywołaj kolejne narzędzie przez <tool_call>. Nie opisuj co zamierzasz zrobić — po prostu to zrób.`;

          // Always use role:'user' for tool results (both success and error).
          // role:'tool' + toolCallId causes HTTP 400 on models that don't support
          // native tool calling (e.g. freehuntx/qwen3-coder:8b).
          // Strip toolCalls from the last assistant message to avoid orphan tool_calls.
          const lastAssistantIdx = nextHistory.length - 1 - [...nextHistory].reverse().findIndex((m: any) => m.role === 'assistant');
          if (lastAssistantIdx !== -1 && lastAssistantIdx < nextHistory.length && nextHistory[lastAssistantIdx].toolCalls?.length) {
            nextHistory[lastAssistantIdx] = {
              ...nextHistory[lastAssistantIdx],
              toolCalls: undefined,
            };
          }
          if (isError) {
            // Increment per-call failure counter (dla identycznych argumentów)
            const fc = (loopGuardRef.current.failCounts.get(callSig) || 0) + 1;
            loopGuardRef.current.failCounts.set(callSig, fc);

            // Increment per-tool failure counter (dla wszystkich prób tego narzędzia)
            const tc = (loopGuardRef.current.toolFailCounts.get(call.functionCall.name) || 0) + 1;
            loopGuardRef.current.toolFailCounts.set(call.functionCall.name, tc);
          } else {
            // Mark jako sukces — zablokuj powtórzenie tej samej komendy
            loopGuardRef.current.successCalls.add(callSig);

            // Czyść liczniki błędów - udana komenda resetuje failure state dla nazwy narzędzia
            loopGuardRef.current.failCounts.delete(callSig);
            loopGuardRef.current.toolFailCounts.delete(call.functionCall.name);
          }
          nextHistory.push({
            role: 'user',
            content: contextToolContent,
          });
        }

        // ── ABORT GUARD przed rekurencją ──────────────────────────────────
        if (abortControllerRef.current?.signal.aborted) {
          addLog?.('WARN', 'Agent loop aborted by user (before recursion)', 'Architect');
          return;
        }

        // Provide tool output back to agent
        await sendToAgent("", [], nextHistory);
      }
    } catch (err: any) {
      setMessages(prev => [...prev, { role: 'system', content: `FATAL: ${err.message || 'Neural Link Interrupted.'}` }]);
      addLog?.('ERROR', `Architect fatal error: ${err.message || 'Neural Link Interrupted'}`, 'Architect');
    } finally {
      if (!existingHistory) {
        setLoading(false);
        lastSpeechTime.current = Date.now();
        abortControllerRef.current = null;
      }
    }
  };

  const handleSend = async () => {
    if ((!input.trim() && pendingFiles.length === 0) || loading) return;
    const userMsg = input || (pendingFiles.length > 0 ? `[Attached ${pendingFiles.length} file(s)]` : '');
    const attachments = [...pendingFiles];
    setInput('');
    setPendingFiles([]);
    setMessages(prev => [...prev, { role: 'user', content: userMsg, attachments }]);

    // Log snapshot requests
    if (userMsg.toLowerCase().includes('snapshot') || userMsg.toLowerCase().includes('co widzisz') || userMsg.toLowerCase().includes('zrób zdjęcie')) {
      addLog?.('INFO', 'Snapshot request initiated', 'Architect');
      requestVisionSnapshot();
    }
    await sendToAgent(userMsg, attachments);
  };

  // Phase 3: Character card management
  const saveCharacterCard = useCallback((name: string) => {
    const newCard: CharacterCard = {
      id: `card-${Date.now()}`,
      name,
      systemPrompt,
      createdAt: new Date().toISOString(),
      lastUsed: new Date().toISOString()
    };

    const updated = [...characterCards, newCard];
    setCharacterCards(updated);
    localStorage.setItem('omnigate_character_cards', JSON.stringify(updated));
    setActiveCardId(newCard.id);
  }, [systemPrompt, characterCards]);

  const loadCharacterCard = useCallback((cardId: string) => {
    const card = characterCards.find(c => c.id === cardId);
    if (card) {
      setSystemPrompt(card.systemPrompt);
      setActiveCardId(cardId);

      // Update lastUsed
      const updated = characterCards.map(c =>
        c.id === cardId ? { ...c, lastUsed: new Date().toISOString() } : c
      );
      setCharacterCards(updated);
      localStorage.setItem('omnigate_character_cards', JSON.stringify(updated));
    }
  }, [characterCards]);

  const deleteCharacterCard = useCallback((cardId: string) => {
    const updated = characterCards.filter(c => c.id !== cardId);
    setCharacterCards(updated);
    localStorage.setItem('omnigate_character_cards', JSON.stringify(updated));
    if (activeCardId === cardId) {
      setActiveCardId(null);
      setSystemPrompt(DEFAULT_SYSTEM_PROMPT);
    }
  }, [characterCards, activeCardId]);

  // Phase 4: Request vision snapshot
  const requestVisionSnapshot = useCallback(() => {
    setMessages(prev => [...prev, { role: 'system', content: '>>> INICJOWANIE PROTOKOŁU SNAPSHOT (VISION LINK)...' }]);
    addLog?.('INFO', 'Initiating snapshot protocol (Vision Link)', 'Architect');

    // Send command to Vision component via Orchestration
    sendMessage({
      from: 'master',
      to: 'vision',
      type: 'command',
      payload: { action: 'request_snapshot' }
    });
  }, [sendMessage, addLog]);

  const removeMessage = (index: number) => {
    setMessages(prev => prev.filter((_, i) => i !== index));
    addLog?.('DEBUG', `Message at index ${index} removed from context`, 'Architect');
  };

  // ── History management ─────────────────────────────────────────────────────
  const startNewConversation = useCallback(() => {
    // Auto-title current if it has meaningful messages
    const userMsgs = messages.filter(m => m.role === 'user');
    if (userMsgs.length > 0 && currentConvId) {
      const firstMsg = userMsgs[0].content.substring(0, 40);
      const convos = ChatPersistence.getAllConversations();
      const current = convos.find(c => c.id === currentConvId);
      if (current && current.title.startsWith('Sesja')) {
        const updated = { ...current, title: firstMsg + (firstMsg.length >= 40 ? '…' : '') };
        // patch title in storage
        const all = convos.map(c => c.id === currentConvId ? updated : c);
        localStorage.setItem('omnigate_conversations', JSON.stringify(all));
      }
    }

    const title = `Sesja ${new Date().toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`;
    const newConvo = ChatPersistence.createConversation(title);
    refreshConversations();
    setCurrentConvId(newConvo.id);
    setMessages([{ role: 'assistant', content: 'OmniGate Architekt Neuronowy zainicjowany. Node-01 protokół konfiguracji w gotowości.' }]);
    setShowHistoryPanel(false);
    addLog?.('INFO', `New conversation started: ${title}`, 'Architect');
  }, [messages, currentConvId, refreshConversations, addLog]);

  const restoreConversation = useCallback((conv: Conversation) => {
    // Save current conv messages
    if (currentConvId) {
      const all = ChatPersistence.getAllConversations();
      const cur = all.find(c => c.id === currentConvId);
      if (cur) {
        const updatedMsgs = messages
          .filter(m => m.role !== 'system' || !m.content.startsWith('FATAL:'))
          .map(m => ({
            id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
            role: m.role as 'user' | 'assistant' | 'system',
            content: m.rawContent || m.content,
            timestamp: new Date().toISOString(),
          }));
        const patchedAll = all.map(c => c.id === currentConvId ? { ...c, messages: updatedMsgs, updatedAt: new Date().toISOString(), isActive: false } : { ...c, isActive: false });
        localStorage.setItem('omnigate_conversations', JSON.stringify(patchedAll));
      }
    }

    // Restore archived conversation
    ChatPersistence.setActiveConversation(conv.id);
    setCurrentConvId(conv.id);

    // Restore messages from saved conversation
    const restoredMessages: Message[] = conv.messages.map(m => ({
      role: m.role as 'user' | 'assistant' | 'system',
      content: m.content,
      rawContent: m.content,
    }));

    setMessages(restoredMessages.length > 0 ? restoredMessages : [
      { role: 'assistant', content: 'OmniGate Architekt Neuronowy zainicjowany. Node-01 protokół konfiguracji w gotowości.' }
    ]);

    setShowHistoryPanel(false);
    refreshConversations();
    addLog?.('INFO', `Conversation restored: ${conv.title}`, 'Architect');
  }, [currentConvId, messages, refreshConversations, addLog]);

  // ── Context Condensation ─────────────────────────────────────────────────────
  const condenseContext = useCallback(async () => {
    const condensableMessages = messages.filter(m => m.role === 'user' || m.role === 'assistant');
    if (condensableMessages.length < 6) {
      addLog?.('WARN', 'Za mało wiadomości do kondensacji (min 6 wiad. user/assistant)', 'Architect');
      return;
    }

    setIsCondensing(true);
    addLog?.('INFO', 'Rozpoczynam kondensację kontekstu...', 'Architect');

    try {
      // Keep last 4 messages intact (most recent context), condense the rest
      const toCondense = condensableMessages.slice(0, -4);
      const keepFromIdx = messages.indexOf(condensableMessages[condensableMessages.length - 4]);
      const toKeep = messages.slice(keepFromIdx);

      // Include tool results associated with condensable messages
      const condenseSummary = messages
        .filter(m => {
          if (m.role === 'user' || m.role === 'assistant') return toCondense.includes(m);
          if (m.role === 'tool') {
            const idx = messages.indexOf(m);
            return idx < keepFromIdx; // tool results before the keep boundary
          }
          return false;
        })
        .map(m => {
          if (m.role === 'tool') {
            const text = (m.contextContent || m.content).substring(0, 300);
            return `[TOOL ${m.name}]: ${text}`;
          }
          const prefix = m.role === 'user' ? 'USER' : 'ASSISTANT';
          const text = (m.contextContent || m.rawContent || m.content).substring(0, 600);
          return `[${prefix}]: ${text}`;
        }).join('\n');

      // Ask the AI to summarize
      const summaryResult = await aiService.runAgentSession(
        `Skondensuj poniższą rozmowę w zwięzłe streszczenie po polsku (max 300 słów). Zachowaj: kluczowe pytania użytkownika, decyzje, nazwy wykorzystanych narzędzi, ich wyniki, ustalenia techniczne. Format: zwięzłe punkty. NIE dodawaj komentarzy — TYLKO streszczenie:\n\n${condenseSummary}`,
        [{ role: 'user', content: 'Streść poniższą rozmowę zachowując kluczowe fakty.' }],
        false, // no MCP tools for condensation
        undefined,
        undefined,
        maxContextTokens
      );

      const summary = summaryResult.text || 'Błąd kondensacji.';
      const summaryTokens = estimateTokens(summary);
      const originalTokens = toCondense.reduce((s, m) => s + estimateMessageTokens(m), 0);
      const savedTokens = Math.max(0, originalTokens - summaryTokens);

      // Use 'assistant' role so the model treats this as prior context, not a user message
      const condensedMsg: Message = {
        role: 'assistant',
        content: `📋 Kontekst skondensowany (${toCondense.length} wiad. → streszczenie, oszczędność: ~${savedTokens} tokenów):\n\n${summary}`,
        rawContent: summary,
        contextContent: summary,
        isCondensed: true,
      };

      // Replace old messages with condensed summary + keep recent intact
      setMessages([condensedMsg, ...toKeep]);

      addLog?.('INFO', `Kontekst skondensowany: ${toCondense.length} wiadomości → streszczenie (~${savedTokens} tokenów zaoszczędzonych)`, 'Architect');
    } catch (e: any) {
      addLog?.('ERROR', `Kondensacja nie powiodła się: ${e.message}`, 'Architect');
    } finally {
      setIsCondensing(false);
    }
  }, [messages, addLog]);

  const deleteConversation = useCallback((convId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (convId === currentConvId) return; // can't delete active
    ChatPersistence.deleteConversation(convId);
    refreshConversations();
  }, [currentConvId, refreshConversations]);

  // Auto-save current session messages to persistence every 10s
  useEffect(() => {
    if (!currentConvId) return;
    const interval = setInterval(() => {
      const all = ChatPersistence.getAllConversations();
      const userMsgs = messages.filter(m => m.role === 'user' || m.role === 'assistant');
      const persistedMsgs = userMsgs.map(m => ({
        id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
        role: m.role as 'user' | 'assistant' | 'system',
        content: m.rawContent || m.content,
        timestamp: new Date().toISOString(),
        conversationId: currentConvId,
      }));
      const updated = all.map(c => c.id === currentConvId
        ? { ...c, messages: persistedMsgs, updatedAt: new Date().toISOString() }
        : c
      );
      localStorage.setItem('omnigate_conversations', JSON.stringify(updated));
    }, 10000);
    return () => clearInterval(interval);
  }, [currentConvId, messages]);


  // ── Auto Context Condensation ─────────────────────────────────────────────
  // Gdy rozmowa przekracza 30 wiadomości user/assistant → auto-kondensacja
  // (30 = ~15 tur dialogu, po tym modele 8B zaczynają "zapominać" początek)
  const AUTO_CONDENSE_THRESHOLD = 30;
  useEffect(() => {
    const userAssistantMsgs = messages.filter(m => m.role === 'user' || m.role === 'assistant');
    const hasCondensed = messages.some(m => m.isCondensed);
    if (
      userAssistantMsgs.length >= AUTO_CONDENSE_THRESHOLD &&
      !isCondensing &&
      !loading &&
      !hasCondensed // nie kondensuj jeśli już jest skondensowane podsumowanie
    ) {
      addLog?.('WARN', `Kontekst przekroczył ${AUTO_CONDENSE_THRESHOLD} wiadomości — auto-kondensacja.`, 'Architect');
      condenseContext();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length]);


  return (
    <div className={`flex flex-col ${auditMode ? 'h-[calc(100vh-180px)]' : 'h-[calc(100vh-220px)]'} min-h-[600px] max-h-[90vh] bg-[#1e222d] rounded border border-[#2a2e39] overflow-hidden shadow-2xl relative`}>

      {/* ── Heuristic Auto-Correction Popup ─────────────────────────────────── */}
      <HeuristicPopup
        blocks={heuristicBlocks}
        visible={heuristicPopupVisible}
        onRepair={handleHeuristicRepair}
        onSkip={() => { setHeuristicPopupVisible(false); addLog?.('INFO', 'Heuristic: pominięto naprawę kodu', 'HeuristicEngine'); }}
        onExecuteOriginal={(code) => {
          setHeuristicPopupVisible(false);
          // Send code to code executor if available
          setInput(`Uruchom ten kod:\n\`\`\`python\n${code}\n\`\`\``);
        }}
      />
      {showBrowserTaskPopup && <BrowserTaskPopup onClose={() => setShowBrowserTaskPopup(false)} />}
      <TranslationPopup
        visible={showTranslationPopup}
        onClose={() => { setShowTranslationPopup(false); setTranslationStatus('idle'); }}
        originalText={translationOriginal}
        translatedText={translationResult}
        streamingText={translationStreaming}
        status={translationStatus}
        sourceLang={sourceLang}
        targetLang={targetLang}
        detectedLang={translationDetectedLang}
        durationMs={translationDurationMs}
        glossaryApplied={translationGlossaryApplied}
        onCopyToInput={(text) => { setInput(text); setShowTranslationPopup(false); }}
        onAddGlossary={handleAddGlossary}
      />
      <ThinkingSessionPopup
        visible={showThinkingPopup}
        settings={thinkingSettings}
        setSettings={setThinkingSettings}
        steps={thinkingSteps}
        running={thinkingRunning}
        onClose={() => setShowThinkingPopup(false)}
        onClear={() => setThinkingSteps([])}
      />

      {/* ── History Sidebar Panel ──────────────────────────────────────────── */}
      {showHistoryPanel && (
        <div className="absolute inset-y-0 left-0 w-64 bg-[#0d1117] border-r border-[#2a2e39] z-50 flex flex-col shadow-2xl">
          <div className="p-2 border-b border-[#2a2e39] flex items-center justify-between bg-[#131722]">
            <span className="text-[9px] font-black text-purple-400 uppercase tracking-widest">
              <i className="fas fa-history mr-1"></i>Historia rozmów
            </span>
            <button onClick={() => setShowHistoryPanel(false)} className="text-slate-500 hover:text-white text-xs">
              <i className="fas fa-times"></i>
            </button>
          </div>

          {/* New conversation button */}
          <button
            onClick={startNewConversation}
            className="mx-2 mt-2 py-1.5 rounded border border-purple-500/40 bg-purple-600/10 text-purple-400 text-[9px] font-bold uppercase tracking-wider hover:bg-purple-600/30 transition-all flex items-center justify-center gap-1"
          >
            <i className="fas fa-plus"></i> Nowa rozmowa
          </button>

          {/* Conversation list */}
          <div className="flex-1 overflow-y-auto custom-scrollbar space-y-1 p-2 mt-1">
            {conversations.length === 0 && (
              <p className="text-[8px] text-slate-600 italic text-center mt-4">Brak zapisanych rozmów</p>
            )}
            {conversations.map(conv => (
              <div
                key={conv.id}
                onClick={() => restoreConversation(conv)}
                className={`group relative p-2 rounded cursor-pointer transition-all ${conv.id === currentConvId
                  ? 'bg-purple-600/20 border border-purple-500/40'
                  : 'border border-transparent hover:bg-slate-800/50 hover:border-[#2a2e39]'
                  }`}
              >
                <div className="flex items-start justify-between gap-1">
                  <div className="flex-1 min-w-0">
                    <p className="text-[9px] text-slate-300 truncate font-medium">{conv.title}</p>
                    <p className="text-[7px] text-slate-600 mt-0.5">
                      {new Date(conv.updatedAt).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      {' · '}{conv.messages.length} wiad.
                    </p>
                  </div>
                  {conv.id === currentConvId ? (
                    <span className="text-[7px] text-purple-400 font-bold shrink-0">AKTYWNA</span>
                  ) : (
                    <button
                      onClick={(e) => deleteConversation(conv.id, e)}
                      className="text-[8px] text-slate-600 hover:text-red-500 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Usuń"
                    >
                      <i className="fas fa-trash-alt"></i>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Stats */}
          <div className="p-2 border-t border-[#2a2e39] text-[7px] text-slate-600">
            {conversations.length} rozmów · {conversations.reduce((s, c) => s + c.messages.length, 0)} wiadomości
          </div>
        </div>
      )}

      {/* ── Context Settings Panel ─────────────────────────────────────────── */}
      {showContextSettings && (
        <div className="absolute top-10 right-2 w-64 bg-[#0d1117] border border-[#2a2e39] rounded shadow-2xl z-50 p-3 space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-[9px] font-black text-slate-300 uppercase tracking-widest">
              <i className="fas fa-sliders-h mr-1 text-purple-400"></i>Okno Kontekstu
            </span>
            <button onClick={() => setShowContextSettings(false)} className="text-slate-500 hover:text-white text-xs">
              <i className="fas fa-times"></i>
            </button>
          </div>

          {/* Current usage bar */}
          <div className="space-y-1">
            <div className="flex justify-between text-[8px]">
              <span className="text-slate-500">Użyte tokeny</span>
              <span style={{ color: contextColor }} className="font-bold">{usedContextTokens.toLocaleString()} / {maxContextTokens.toLocaleString()}</span>
            </div>
            <div className="w-full h-1.5 bg-[#1e222d] rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ width: `${contextPercent}%`, backgroundColor: contextColor }}
              />
            </div>
            <p className="text-[7px] text-slate-600">~{contextPercent}% okna kontekstu zajęte</p>
          </div>

          {/* Max context slider */}
          <div className="space-y-1">
            <div className="flex justify-between items-center">
              <span className="text-[8px] text-slate-400">Maks. okno kontekstu</span>
              <span className="text-[8px] font-bold text-purple-400">{(maxContextTokens / 1000).toFixed(0)}K</span>
            </div>
            <input
              type="range"
              min={2000}
              max={128000}
              step={1000}
              value={maxContextTokens}
              onChange={e => setMaxContextTokens(parseInt(e.target.value, 10))}
              className="w-full accent-purple-500 cursor-pointer"
            />
            <div className="flex justify-between text-[7px] text-slate-600">
              <span>2K</span><span>32K</span><span>64K</span><span>128K</span>
            </div>
          </div>

          {/* Preset buttons */}
          <div className="flex gap-1 flex-wrap">
            {[4096, 8000, 16000, 32000, 128000].map(preset => (
              <button
                key={preset}
                onClick={() => setMaxContextTokens(preset)}
                className={`text-[8px] px-2 py-0.5 rounded border transition-all ${maxContextTokens === preset
                  ? 'border-purple-500 bg-purple-600/20 text-purple-400'
                  : 'border-[#2a2e39] text-slate-500 hover:text-slate-300'
                  }`}
              >
                {preset >= 1000 ? `${preset / 1000}K` : preset}
              </button>
            ))}
          </div>

          {/* Condense context button */}
          <button
            onClick={() => { condenseContext(); }}
            disabled={isCondensing || messages.filter(m => m.role === 'user' || m.role === 'assistant').length < 6}
            className={`w-full py-1.5 rounded border text-[8px] font-bold uppercase tracking-wider transition-all ${isCondensing
              ? 'border-blue-500/30 bg-blue-600/10 text-blue-400 animate-pulse cursor-wait'
              : 'border-cyan-500/30 bg-cyan-600/10 text-cyan-400 hover:bg-cyan-600/25'
              } disabled:opacity-40 disabled:cursor-not-allowed`}
          >
            <i className={`fas ${isCondensing ? 'fa-spinner fa-spin' : 'fa-compress-arrows-alt'} mr-1`}></i>
            {isCondensing ? 'Kondensacja...' : 'Kondensuj kontekst'}
          </button>

          {/* Clear context button */}
          <button
            onClick={() => {
              setMessages([{ role: 'assistant', content: 'Kontekst wyczyszczony. OmniGate gotowy.' }]);
              setShowContextSettings(false);
              addLog?.('INFO', 'Context cleared by operator', 'Architect');
            }}
            className="w-full py-1 rounded border border-red-500/30 bg-red-600/10 text-red-400 text-[8px] font-bold uppercase tracking-wider hover:bg-red-600/20 transition-all"
          >
            <i className="fas fa-broom mr-1"></i>Wyczyść kontekst
          </button>
        </div>
      )}

      {/* ── Top Header Bar ──────────────────────────────────────────────────── */}
      <div className="p-3 border-b border-[#2a2e39] bg-[#131722] flex items-center justify-between">
        <div className="flex items-center gap-2">
          {/* History toggle */}
          <button
            onClick={() => { setShowHistoryPanel(!showHistoryPanel); setShowContextSettings(false); refreshConversations(); }}
            className={`flex items-center gap-1 px-2 py-1 rounded transition-all ${showHistoryPanel ? 'bg-purple-500/20 text-purple-400 border border-purple-500/50' : 'text-slate-600 hover:text-slate-400'
              }`}
            title="Historia rozmów"
          >
            <i className="fas fa-history text-[10px]"></i>
          </button>
          <div className={`w-1.5 h-1.5 rounded-full ${isSpeaking ? 'bg-green-500 animate-ping' : 'bg-purple-500 animate-pulse'}`}></div>
          <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest">Architekt Neuronowy</span>
          {activeCardId && (
            <span className="text-[8px] bg-purple-500/20 text-purple-400 px-1 py-0.5 rounded">
              {characterCards.find(c => c.id === activeCardId)?.name || 'Custom'}
            </span>
          )}
        </div>

        {/* Context counter pill */}
        <button
          onClick={() => { setShowContextSettings(!showContextSettings); setShowHistoryPanel(false); }}
          className="flex items-center gap-1.5 px-2 py-1 rounded border transition-all hover:opacity-90"
          style={{
            borderColor: contextColor + '60',
            backgroundColor: contextColor + '15',
          }}
          title={`Kontekst: ${usedContextTokens} / ${maxContextTokens} tokenów (~${contextPercent}%)`}
        >
          <div className="w-16 h-1 bg-[#1e222d] rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{ width: `${contextPercent}%`, backgroundColor: contextColor }}
            />
          </div>
          <span className="text-[8px] font-bold" style={{ color: contextColor }}>
            {usedContextTokens >= 1000 ? `${(usedContextTokens / 1000).toFixed(1)}K` : usedContextTokens}
            <span className="text-slate-600 font-normal">/{(maxContextTokens / 1000).toFixed(0)}K</span>
          </span>
        </button>

        <div className="flex items-center gap-2 flex-wrap justify-end">
          <div className="flex items-center gap-1 border-r border-[#2a2e39] pr-1">
            <select
              value={selectedProvider}
              onChange={(e) => {
                const newProv = e.target.value;
                setSelectedProvider(newProv);
                const firstM = availableModels.find((m) => !newProv || (m.provider || 'Unknown') === newProv);
                handleEndpointChange(firstM ? firstM.id : '');
              }}
              className="bg-slate-700 text-slate-300 text-[9px] px-1 py-1 rounded outline-none border border-[#2a2e39] max-w-[90px] truncate"
              title="Provider"
            >
              <option value="">Wszystkie (All)</option>
              {Array.from(new Set(availableModels.map(m => m.provider || 'Unknown'))).map(provider => (
                <option key={String(provider)} value={String(provider)}>{String(provider).toUpperCase()}</option>
              ))}
            </select>
            <select
              value={selectedEndpoint}
              onChange={(e) => handleEndpointChange(e.target.value)}
              className="bg-slate-700 text-slate-300 text-[9px] px-1 py-1 rounded outline-none border border-[#2a2e39] max-w-[100px] truncate"
              title="Neural Chat Endpoint"
            >
              <option value="" disabled>-- Wybierz model --</option>
              {Object.entries(
                availableModels.reduce((acc, m) => {
                  const p = m.provider || 'Unknown';
                  if (!selectedProvider || selectedProvider === p) {
                    if (!acc[p]) acc[p] = [];
                    acc[p].push(m);
                  }
                  return acc;
                }, {} as Record<string, typeof availableModels>)
              ).map(([provider, models]: [string, any]) => (
                <optgroup key={provider} label={provider.toUpperCase()}>
                  {models.map((m: any) => {
                    const isOllama = m.id.includes('ollama') || provider.toLowerCase() === 'ollama';
                    return <option key={m.id} value={m.id}>{isOllama ? `🦙 ${m.id}` : m.id}</option>;
                  })}
                </optgroup>
              ))}
            </select>
            <button
              onClick={handleTestModels}
              disabled={isTestingModels}
              className={`flex items-center justify-center w-6 h-6 rounded transition-all text-xs ${isTestingModels ? 'text-slate-500 cursor-wait' : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/40 border border-blue-500/50'}`}
              title="Test Active Models"
            >
              <i className={`fas ${isTestingModels ? 'fa-spinner fa-spin' : 'fa-vial'}`}></i>
            </button>
          </div>
          {/* Vision model selector */}
          <div className="flex items-center gap-1 border-r border-[#2a2e39] pr-1" title="Vision model używany przez screenshot VLM i vision_describe_screen">
            <i className="fas fa-eye text-[8px] text-purple-400"></i>
            <select
              value={selectedVisionModel}
              onChange={(e) => {
                setSelectedVisionModel(e.target.value);
                localStorage.setItem('omnigate_vision_model', e.target.value);
              }}
              className="bg-slate-700 text-purple-300 text-[9px] px-1 py-1 rounded outline-none border border-purple-500/30 max-w-[90px] truncate"
              title="VLM — model używany do analizy screenshotów"
            >
              {(availableModels.some(m => m.tags?.includes('vision')) 
                 ? availableModels.filter(m => m.tags?.includes('vision')).map(m => ({id: m.id, label: m.id}))
                 : NIM_VISION_OPTIONS
              ).map(o => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
          </div>
          {/* TTS model selector */}
          <div className="flex items-center gap-1 border-r border-[#2a2e39] pr-1" title="TTS model">
            <i className="fas fa-volume-up text-[8px] text-green-400"></i>
            <select
              value={selectedTTSModel}
              onChange={(e) => {
                setSelectedTTSModel(e.target.value);
                localStorage.setItem('omnigate_tts_model', e.target.value);
              }}
              className="bg-slate-700 text-green-300 text-[9px] px-1 py-1 rounded outline-none border border-green-500/30 max-w-[90px] truncate"
              title="TTS Model"
            >
              <option value="">AUTO (Config)</option>
              {availableModels.filter(m => m.tags?.includes('tts') || m.id.includes('tts')).map(m => (
                <option key={m.id} value={m.id}>{m.id}</option>
              ))}
            </select>
          </div>
          {/* TTS speed selector */}
          <div className="flex items-center gap-1 border-r border-[#2a2e39] pr-1" title="TTS Speed">
            <i className="fas fa-tachometer-alt text-[8px] text-yellow-400"></i>
            <select
              value={ttsSpeed}
              onChange={(e) => {
                const val = parseFloat(e.target.value);
                setTtsSpeed(val);
                localStorage.setItem('omnigate_tts_speed', e.target.value);
              }}
              className="bg-slate-700 text-yellow-300 text-[9px] px-1 py-1 rounded outline-none border border-yellow-500/30 w-[45px]"
            >
              {[0.5, 0.75, 1.0, 1.25, 1.5, 1.75, 2.0].map(s => (
                <option key={s} value={s}>{s}x</option>
              ))}
            </select>
          </div>
          {/* ASR model selector */}
          <div className="flex items-center gap-1 border-r border-[#2a2e39] pr-1" title="ASR model">
            <i className="fas fa-microphone text-[8px] text-yellow-400"></i>
            <select
              value={selectedASRModel}
              onChange={(e) => {
                setSelectedASRModel(e.target.value);
                localStorage.setItem('omnigate_asr_model', e.target.value);
              }}
              className="bg-slate-700 text-yellow-300 text-[9px] px-1 py-1 rounded outline-none border border-yellow-500/30 max-w-[90px] truncate"
              title="ASR Model"
            >
              <option value="">AUTO (Config)</option>
              {availableModels.filter(m => m.tags?.includes('asr') || m.id.includes('whisper')).map(m => (
                <option key={m.id} value={m.id}>{m.id}</option>
              ))}
            </select>
          </div>
          {/* Translation language selectors + status indicator */}
          <div className="flex items-center gap-1 border-r border-[#2a2e39] pr-1">
            {/* Status indicator dot */}
            <div
              title={`Translation: ${translationStatus}`}
              className={`w-2 h-2 rounded-full transition-all ${
                translationStatus === 'idle' ? 'bg-slate-600' :
                translationStatus === 'detecting' ? 'bg-yellow-400 animate-ping' :
                translationStatus === 'translating' ? 'bg-blue-400 animate-pulse' :
                translationStatus === 'done' ? 'bg-emerald-400' :
                'bg-red-500'
              }`}
            />
            <i className="fas fa-language text-[8px] text-blue-400"></i>
            {/* Source language */}
            <select
              value={sourceLang}
              onChange={e => setSourceLang(e.target.value)}
              className="bg-slate-700 text-blue-300 text-[9px] px-1 py-1 rounded outline-none border border-blue-500/30 max-w-[52px]"
              title="Source language (auto = detect)"
            >
              {TRANSLATION_LANGUAGES.map(l => (
                <option key={l.code} value={l.code}>{l.label}</option>
              ))}
            </select>
            <span className="text-slate-600 text-[8px]">→</span>
            {/* Target language */}
            <select
              value={targetLang}
              onChange={e => setTargetLang(e.target.value)}
              className="bg-slate-700 text-blue-300 text-[9px] px-1 py-1 rounded outline-none border border-blue-500/30 max-w-[52px]"
              title="Target language"
            >
              {TRANSLATION_LANGUAGES.filter(l => l.code !== 'auto').map(l => (
                <option key={l.code} value={l.code}>{l.label}</option>
              ))}
            </select>
            {/* Translate button — translates current input or last user message */}
            <button
              onClick={() => {
                const text = input.trim() || (messages.filter(m => m.role === 'user').slice(-1)[0]?.content ?? '');
                handleTranslate(text);
              }}
              disabled={translationStatus === 'detecting' || translationStatus === 'translating'}
              className={`flex items-center justify-center w-5 h-5 rounded text-[8px] transition-all ${
                translationStatus === 'translating' || translationStatus === 'detecting'
                  ? 'bg-blue-500/10 text-blue-500 cursor-wait'
                  : 'bg-blue-600/20 text-blue-400 hover:bg-blue-600/40 border border-blue-500/30'
              }`}
              title="Translate (input or last message)"
            >
              <i className={`fas ${
                translationStatus === 'translating' ? 'fa-circle-notch fa-spin' :
                translationStatus === 'detecting' ? 'fa-search fa-pulse' :
                'fa-play'
              }`}></i>
            </button>
          </div>
          {/* Phase 3: System prompt editor toggle */}
          <button
            onClick={() => setShowPromptEditor(!showPromptEditor)}
            className={`flex items-center gap-1 px-2 py-1 rounded transition-all ${showPromptEditor ? 'bg-orange-500/20 text-orange-400 border border-orange-500/50' : 'text-slate-600'}`}
            title="System Prompt"
          >
            <i className="fas fa-cog text-[10px]"></i>
            <span className="text-[8px] font-bold uppercase hidden sm:inline">Prompt</span>
          </button>
          <button
            onClick={() => {
              setVoiceMode(!voiceMode);
              addLog?.('INFO', `Voice mode ${!voiceMode ? 'enabled' : 'disabled'}`, 'Architect');
            }}
            className={`flex items-center gap-1 px-2 py-1 rounded transition-all ${voiceMode ? 'bg-purple-500/20 text-purple-400 border border-purple-500/50' : 'text-slate-600'}`}
            title="Toggle Voice"
          >
            <i className={`fas ${voiceMode ? 'fa-volume-up' : 'fa-volume-mute'} text-[10px]`}></i>
            <span className="text-[8px] font-bold uppercase hidden sm:inline">Voice</span>
          </button>
          <button
            onClick={() => {
              setHandsFreeMode(!handsFreeMode);
              addLog?.('INFO', `Hands-free mode ${!handsFreeMode ? 'enabled' : 'disabled'}`, 'Architect');
            }}
            className={`flex items-center gap-1 px-2 py-1 rounded transition-all ${handsFreeMode ? 'bg-green-500/20 text-green-400 border border-green-500/50' : 'text-slate-600'}`}
            title="Toggle Hands-Free"
          >
            <i className="fas fa-hands-helping text-[10px]"></i>
            <span className="text-[8px] font-bold uppercase hidden sm:inline">Handsfree</span>
          </button>

          <button
            onClick={() => setShowThinkingPopup(v => !v)}
            className={`flex items-center gap-1 px-2 py-1 rounded transition-all ${showThinkingPopup
              ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/50'
              : 'text-slate-600'
              }`}
            title="Sequential Thinking Console"
          >
            <i className={`fas ${thinkingRunning ? 'fa-brain fa-pulse' : 'fa-brain'} text-[10px]`}></i>
            <span className="text-[8px] font-bold uppercase hidden sm:inline">Thinking</span>
          </button>

          {/* ⚡ Auto-Correction Mode Toggle */}
          <button
            onClick={() => {
              const newVal = !autocorrectMode;
              setAutocorrectMode(newVal);
              localStorage.setItem('omnigate_autocorrect', String(newVal));
              addLog?.('INFO', `Auto-Correction ReAct Mode: ${newVal ? 'WŁĄCZONY' : 'WYŁĄCZONY'}`, 'HeuristicEngine');
            }}
            className={`flex items-center gap-1 px-2 py-1 rounded transition-all ${autocorrectMode
              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50'
              : 'text-slate-600 border border-transparent'
              }`}
            title={`Auto-Correction ReAct Mode: ${autocorrectMode ? 'ON' : 'OFF'} — Heuristic Engine + Skills Context`}
            id="autocorrect-mode-toggle"
          >
            <i className={`fas fa-shield-alt text-[10px] ${autocorrectMode ? 'text-emerald-400' : ''}`}></i>
            <span className="text-[8px] font-bold uppercase hidden sm:inline">ReAct</span>
            {autocorrectMode && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse hidden sm:inline-block"></span>}
          </button>

          {/* Reasoning & Tool Calling Toggles */}
          <button
            onClick={() => {
              const newVal = !reasoningMode;
              setReasoningMode(newVal);
              localStorage.setItem('omnigate_reasoning', String(newVal));
            }}
            className={`flex items-center gap-1 px-2 py-1 rounded transition-all ${reasoningMode ? 'bg-orange-600/20 text-orange-400 border border-orange-500/50' : 'text-slate-600 border border-transparent'}`}
            title="Enable Reasoning (CoT) for supported models like Mistral"
          >
            <i className="fas fa-lightbulb text-[10px]"></i>
            <span className="text-[8px] font-bold uppercase hidden sm:inline">Reasoning</span>
          </button>
          
          <button
            onClick={() => {
              const newVal = !toolCallingMode;
              setToolCallingMode(newVal);
              localStorage.setItem('omnigate_tool_calling', String(newVal));
            }}
            className={`flex items-center gap-1 px-2 py-1 rounded transition-all ${toolCallingMode ? 'bg-blue-600/20 text-blue-400 border border-blue-500/50' : 'text-slate-600 border border-transparent'}`}
            title="Enable MCP Tool Calling"
          >
            <i className="fas fa-tools text-[10px]"></i>
            <span className="text-[8px] font-bold uppercase hidden sm:inline">Tools</span>
          </button>

          <div className="relative">
            <button
              onClick={() => setShowServerPicker(!showServerPicker)}
              className={`flex items-center gap-1 px-2 py-1 rounded transition-all ${mcpEnabled ? 'bg-blue-500/20 text-blue-400 border border-blue-500/50' : 'text-slate-600'}`}
              title="Manual MCP Server Selection"
            >
              <i className={`fas ${mcpEnabled ? 'fa-plug' : 'fa-unlink'} text-[10px]`}></i>
              <span className="text-[8px] font-bold uppercase hidden sm:inline">MCP ({activeMcpServers.size})</span>
            </button>

            {showServerPicker && (
              <div className="absolute top-full right-0 mt-1 w-48 bg-[#131722] border border-[#2a2e39] rounded shadow-2xl z-[100] p-2 space-y-1">
                <div className="flex justify-between items-center px-1 mb-1">
                  <span className="text-[7px] font-black text-slate-500 uppercase">Servers</span>
                  <button
                    onClick={() => setMcpEnabled(!mcpEnabled)}
                    className={`text-[7px] font-bold ${mcpEnabled ? 'text-green-500' : 'text-red-500'}`}
                  >
                    {mcpEnabled ? 'DISABLE ALL' : 'ENABLE ALL'}
                  </button>
                </div>
                <div className="max-h-40 overflow-y-auto custom-scrollbar italic">
                  {mcpServers.map(srv => (
                    <label key={srv} className="flex items-center gap-2 p-1 hover:bg-slate-800 rounded cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={activeMcpServers.has(srv)}
                        onChange={() => toggleMcpServer(srv)}
                        className="w-2.5 h-2.5 accent-blue-500"
                      />
                      <span className="text-[9px] text-slate-300 group-hover:text-white truncate">{srv}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Phase 3: System Prompt Editor Panel */}
      {showPromptEditor && (
        <div className="p-3 border-b border-[#2a2e39] bg-[#131722]/80 space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-[9px] font-black text-slate-400 uppercase">System Prompt</span>
            <div className="flex gap-2">
              <select
                value={activeCardId || ''}
                onChange={(e) => e.target.value && loadCharacterCard(e.target.value)}
                className="bg-slate-700 text-slate-300 text-[9px] px-2 py-1 rounded"
              >
                <option value="">-- Załaduj kartę --</option>
                {characterCards.map(card => (
                  <option key={card.id} value={card.id}>{card.name}</option>
                ))}
              </select>
              <button
                onClick={() => {
                  const name = prompt('Nazwa karty:');
                  if (name) saveCharacterCard(name);
                }}
                className="text-[9px] bg-purple-600 text-white px-2 py-1 rounded hover:bg-purple-500"
              >
                Zapisz
              </button>
              <button
                onClick={() => setSystemPrompt(DEFAULT_SYSTEM_PROMPT)}
                className="text-[9px] bg-slate-600 text-white px-2 py-1 rounded hover:bg-slate-500"
              >
                Reset
              </button>
            </div>
          </div>
          <textarea
            value={systemPrompt}
            onChange={(e) => setSystemPrompt(e.target.value)}
            className="w-full bg-[#1e222d] border border-[#2a2e39] rounded p-2 text-[10px] text-slate-300 h-24 resize-none font-mono focus:border-purple-500/50 outline-none"
            placeholder="System prompt dla agenta..."
          />
        </div>
      )}

      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto p-4 space-y-4 font-mono text-[10px] custom-scrollbar"
        onDragOver={e => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={e => { e.preventDefault(); setIsDragOver(false); if (e.dataTransfer.files.length) handleFileDrop(e.dataTransfer.files); }}
      >
        {/* Drag overlay */}
        {isDragOver && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-purple-900/40 border-4 border-dashed border-purple-500 pointer-events-none">
            <div className="text-center">
              <i className="fas fa-cloud-upload-alt text-5xl text-purple-400 mb-2 block"></i>
              <p className="text-purple-300 font-black text-lg uppercase tracking-widest">Upuść plik do chatu</p>
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'} group`}>
            <div className={`max-w-[85%] p-3 rounded space-y-2 ${m.role === 'user' ? 'bg-purple-600/10 text-purple-200 border border-purple-500/20' :
              m.role === 'system' ? 'bg-[#131722] text-yellow-500 border border-yellow-500/10 italic' :
                m.role === 'tool' ? 'bg-[#0f1115] text-slate-500 border border-[#2a2e39] font-mono overflow-x-auto max-w-full' :
                  'bg-[#131722] text-slate-400 border border-[#2a2e39]'
              }`}>
              {/* Attachments rendered above text */}
              {m.attachments && m.attachments.length > 0 && (
                <div className="space-y-1.5">
                  {m.attachments.map(att => (
                    <div key={att.id}>
                      {att.type === 'image' && (
                        <img src={att.dataUrl} alt={att.name}
                          className="max-w-full max-h-48 rounded border border-purple-500/20 cursor-pointer object-contain"
                          onClick={() => window.open(att.dataUrl, '_blank')}
                        />
                      )}
                      {att.type === 'audio' && (
                        <audio controls src={att.dataUrl} className="w-full h-8" style={{ height: '32px' }} />
                      )}
                      {att.type === 'video' && (
                        <video controls src={att.dataUrl} className="max-w-full max-h-40 rounded border border-purple-500/20" />
                      )}
                      {(att.type === 'pdf' || att.type === 'text' || att.type === 'file') && (
                        <div className="flex items-center gap-2 bg-[#1e222d] border border-[#2a2e39] rounded px-2 py-1.5">
                          <i className={`fas ${att.type === 'pdf' ? 'fa-file-pdf text-red-400' :
                            att.type === 'text' ? 'fa-file-alt text-blue-400' :
                              'fa-file text-slate-400'
                            } text-sm`}></i>
                          <span className="text-[9px] text-slate-300 truncate max-w-[160px]">{att.name}</span>
                          <span className="text-[8px] text-slate-500 ml-auto">{(att.size / 1024).toFixed(1)}KB</span>
                          <a href={att.dataUrl} download={att.name}
                            className="text-[8px] text-purple-400 hover:text-purple-300 ml-1"
                          ><i className="fas fa-download"></i></a>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {m.toolCalls && m.toolCalls.length > 0 && (
                <div className="mb-2 space-y-1">
                  {m.toolCalls.map((tc: any, idx: number) => (
                    <div key={idx} className="bg-[#1e222d] border border-blue-500/30 text-blue-400 p-2 rounded text-[9px] flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <i className="fas fa-tools"></i>
                        <span>Calling: <strong className="text-blue-300">{tc.function?.name}</strong></span>
                      </div>
                      {tc.function?.arguments && (
                        <div className="bg-[#0f1115] p-1.5 rounded text-blue-300 whitespace-pre-wrap overflow-x-auto max-w-full">
                          {typeof tc.function.arguments === 'string' ? tc.function.arguments : JSON.stringify(tc.function.arguments, null, 2)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {m.role === 'tool' ? (
                <ToolResultDisplay content={m.content} name={m.name} />
              ) : m.isCondensed ? (
                <div className="whitespace-pre-wrap text-cyan-300/80">
                  {m.content}
                </div>
              ) : (
                <div className="whitespace-pre-wrap">{m.content}</div>
              )}
              {/* Message Controls */}
              <div className="flex justify-end gap-2 pt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {m.role === 'assistant' && !m.toolCalls?.length && (
                  <button
                    onClick={() => {
                      const blob = new Blob([m.content], { type: 'text/plain' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `omnigate-export-${new Date().getTime()}.txt`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                    }}
                    className="text-[8px] text-cyan-500/50 hover:text-cyan-500 uppercase font-black"
                    title="Eksportuj zawartość do pliku"
                  >
                    <i className="fas fa-file-export mr-1"></i> Eksport
                  </button>
                )}
                <button
                  onClick={() => removeMessage(i)}
                  className="text-[8px] text-red-500/50 hover:text-red-500 uppercase font-black"
                  title="Remove from context"
                >
                  <i className="fas fa-trash-alt mr-1"></i> Usuń z kontekstu
                </button>
              </div>
            </div>
          </div>
        ))}
        {loading && <div className="text-purple-500 animate-pulse font-bold text-[9px] uppercase tracking-tighter">Obliczanie ścieżki neuronowej...</div>}
      </div>

      <div className="p-3 bg-[#131722] border-t border-[#2a2e39] space-y-2">
        {/* Pending attachments preview strip */}
        {pendingFiles.length > 0 && (
          <div className="flex gap-2 flex-wrap p-2 bg-[#0a0c10] border border-[#2a2e39] rounded">
            {pendingFiles.map(att => (
              <div key={att.id} className="relative group">
                {att.type === 'image' ? (
                  <img src={att.dataUrl} alt={att.name} className="w-12 h-12 object-cover rounded border border-purple-500/30" />
                ) : (
                  <div className="w-12 h-12 flex flex-col items-center justify-center bg-[#1e222d] border border-[#2a2e39] rounded text-center">
                    <i className={`fas ${att.type === 'audio' ? 'fa-music text-green-400' :
                      att.type === 'video' ? 'fa-film text-blue-400' :
                        att.type === 'pdf' ? 'fa-file-pdf text-red-400' :
                          'fa-file text-slate-400'
                      } text-sm`}></i>
                    <span className="text-[7px] text-slate-500 truncate w-full px-0.5 mt-0.5">{att.name.slice(0, 6)}</span>
                  </div>
                )}
                <button
                  onClick={() => removePendingFile(att.id)}
                  className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-white text-[8px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >×</button>
              </div>
            ))}
            <button
              onClick={() => setPendingFiles([])}
              className="text-[8px] text-slate-500 hover:text-red-400 self-center ml-1"
            >Wyczyść</button>
          </div>
        )}

        {/* Input row */}
        <div className="flex gap-2">
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,audio/*,video/*,.pdf,.txt,.md,.json,.csv"
            className="hidden"
            onChange={e => e.target.files && handleFileDrop(e.target.files)}
          />
          <button
            onClick={toggleRecording}
            className={`w-9 h-9 rounded flex items-center justify-center transition-all ${isRecording ? 'bg-red-500 text-white animate-pulse shadow-[0_0_10px_red]' : 'bg-[#1e222d] border border-[#2a2e39] text-slate-400 hover:text-white'}`}
          >
            <i className={`fas ${isRecording ? 'fa-stop' : 'fa-microphone'}`}></i>
          </button>
          {/* Attach button */}
          <button
            onClick={() => fileInputRef.current?.click()}
            title="Załącz plik (obraz, audio, video, PDF, TXT)"
            className={`w-9 h-9 rounded flex items-center justify-center transition-all border ${pendingFiles.length > 0
              ? 'bg-purple-500/20 border-purple-500/50 text-purple-400'
              : 'bg-[#1e222d] border-[#2a2e39] text-slate-400 hover:text-purple-400'
              }`}
          >
            {pendingFiles.length > 0
              ? <span className="text-[9px] font-black">{pendingFiles.length}</span>
              : <i className="fas fa-paperclip text-xs"></i>
            }
          </button>
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyPress={e => e.key === 'Enter' && handleSend()}
            placeholder={isRecording ? "Słucham..." : pendingFiles.length > 0 ? `${pendingFiles.length} plik(ów) ready — dodaj opis lub wyślij` : "Wydaj komendę Architektowi..."}
            className="flex-1 bg-[#1e222d] border border-[#2a2e39] rounded px-3 py-2 text-[10px] text-white focus:outline-none focus:border-purple-500/50 font-mono"
            disabled={isRecording}
          />
          {loading && (
            <button
              onClick={handleStop}
              className="w-9 h-9 bg-red-600/30 border border-red-500/50 text-red-500 rounded flex items-center justify-center hover:bg-red-500 hover:text-white transition-all animate-pulse"
              title="STOP / INTERRUPT"
            >
              <i className="fas fa-hand-paper text-xs"></i>
            </button>
          )}
          <button
            onClick={handleSend}
            disabled={isRecording || (loading && !abortControllerRef.current)}
            className={`w-9 h-9 ${loading ? 'bg-slate-700' : 'bg-purple-600 hover:bg-purple-500'} rounded flex items-center justify-center transition-colors disabled:opacity-50`}
            title="Wyślij wiadomość do SuperAssistant"
          >
            <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-chevron-right'} text-xs`}></i>
          </button>
          
          <button
            onClick={async () => {
               const text = input.trim();
               if (!text) return;
               try {
                 const res = await fetch(`/v1/omnigate-use/agent/run`, {
                   method: 'POST',
                   headers: { 'Content-Type': 'application/json' },
                   body: JSON.stringify({ task: text, max_iters: 10 })
                 });
                 const data = await res.json();
                 if (data.task_id) {
                   setInput('');
                   addLog?.('INFO', `Zlecono zadanie agentowi (Task: ${data.task_id})`, 'AgentChat');
                   window.dispatchEvent(new CustomEvent('omnigate-agent-run', { detail: { taskId: data.task_id } }));
                 }
               } catch (e: any) {
                 addLog?.('ERROR', `Błąd zlecenia zadania: ${e.message}`, 'AgentChat');
               }
            }}
            disabled={isRecording || (loading && !abortControllerRef.current) || !input.trim()}
            className={`w-9 h-9 ${!input.trim() ? 'bg-slate-700' : 'bg-orange-600 hover:bg-orange-500'} rounded flex items-center justify-center transition-colors disabled:opacity-50 text-white shadow-[0_0_8px_rgba(234,88,12,0.3)]`}
            title="Zleć autonomiczne zadanie agentowi (Computer Use)"
          >
            <i className="fas fa-robot text-xs"></i>
          </button>
        </div>
      </div>

      {/* ── VIDEO PROCESSOR MODAL (VLM) ────────────────────────────────────────── */}
      {videoFileToProcess && (
        <VideoProcessorPanel
          file={videoFileToProcess}
          onExtract={async (segments: any[], useAudio: boolean, customSystemPrompt: string, synthPromptOverride: string, segmentMaxTok: number, synthMaxTok: number) => {
            const rawFile = videoFileToProcess;
            setVideoFileToProcess(null);
            setLoading(true);

            setMessages(prev => [...prev, {
              role: 'system',
              content: `[System: Rozpoczynam zadanie multimodalne VLM]\nSegmentów do analizy: ${segments.length}\nAudio: ${useAudio ? "w toku (Whisper ASR)" : "Wyłączone"}`
            }]);

            try {
              let audioTranscription = "";
              if (useAudio && rawFile) {
                addLog?.('INFO', `Rozpoczynam transkrypcję audio z wideo...`, 'VLM');
                audioTranscription = await aiService.transcribeAudio(rawFile, selectedASRModel);
                addLog?.('INFO', `Transkrypcja zakończona.`, 'VLM');
              }

              let combinedAnalysis = "";
              for (let i = 0; i < segments.length; i++) {
                const seg = segments[i];
                addLog?.('INFO', `Analizowanie segmentu wizualnego ${seg.index}/${segments.length}...`, 'VLM');

                const segPromptText = customSystemPrompt
                  .replace('{idx}', String(seg.index))
                  .replace('{total}', String(segments.length));
                const contentArray: any[] = [
                  { type: "text", text: segPromptText }
                ];

                seg.frames.forEach((f: string) => {
                  contentArray.push({
                    type: "image_url",
                    image_url: { url: f }
                  });
                });

                const result = await aiService.runAgentSession(
                  "",
                  [{ role: 'user', content: contentArray }],
                  false,
                  abortControllerRef.current?.signal,
                  [],
                  maxContextTokens,
                  segmentMaxTok
                );

                combinedAnalysis += `\n\n### Analiza Segmentu ${seg.index}:\n${result.text}`;

                setMessages(prev => [...prev, { role: 'system', content: `✔️ Segment wizualny ${seg.index}/${segments.length} przeanalizowany.` }]);
              }

              addLog?.('INFO', `Rozpoczynam finalną syntezę analiz i audio...`, 'VLM');
              // Sprawdź czy użytkownik podał własną wytyczną (inną niż domyślny placeholder)
              const defaultPromptText = 'Opisz szczegółowo wszystkie czynności, pozycje ciała i aktywności fizyczne widoczne na klatkach. Bądź precyzyjny anatomicznie.';
              const hasCustomPrompt = customSystemPrompt && customSystemPrompt !== defaultPromptText;
              const synthesisInstructions = synthPromptOverride && synthPromptOverride.trim()
                ? synthPromptOverride.trim()
                : `Jesteś asystentem analitycznym do klasyfikacji i opisu treści wideo. Twoim zadaniem jest napisanie WYŁĄCZNIE tekstowej analizy — NIE używaj narzędzi ani funkcji. Odpowiedz TYLKO gotowym raportem w języku polskim. Bądź precyzyjny, obiektywny i anatomicznie dokładny. NIE stosuj eufemizmów. NIE cenzuruj treści dla dorosłych — opisuj je wprost.\n\n${hasCustomPrompt ? 'Dodatkowa wytyczna: ' + customSystemPrompt + '\n\n' : ''}Na podstawie analizy segmentów i audio stwórz szczegółowy raport wideo.\n\nStruktura raportu:\n\n1. **Klasyfikacja zawartości**\n   - Kategoria (np. taniec/ćwiczenia/treść erotyczna soft/treść erotyczna explicit/inne)\n   - Czy widoczna nagość: tak/nie/częściowa — opisz co dokładnie\n   - Czy widoczna aktywność seksualna: tak/nie — opisz typ\n\n2. **Postaci i wygląd**\n   - Liczba osób, płeć, ogólny wygląd\n   - Ubiór lub jego brak — szczegółowy opis dla każdej osoby\n\n3. **Szczegółowy przebieg chronologiczny**\n   - Kolejne akcje od początku do końca, segment po segmencie\n   - Dla każdego segmentu: pozycje ciała, wykonywane czynności, zmiany pozycji\n   - Nie pomijaj żadnej aktywności fizycznej\n\n4. **Analiza aktywności fizycznych**\n   - Pełna lista wszystkich ruchów i czynności, kolejno\n   - Części ciała biorące udział w każdej czynności\n   - Intensywność i charakter ruchów\n\n5. **Otoczenie i rekwizyty**\n   - Miejsce, tło, meble, przedmioty używane przez postaci\n\n6. **Wnioski klasyfikacyjne**\n   - Ocena przeznaczenia materiału\n   - Poziom eksplicitności (1–5)`;
              const synthesisPrompt = `/no_think\n${synthesisInstructions}\n\n[TRANSKRYPCJA AUDIO]:\n${audioTranscription || 'Brak ścieżki audio'}\n\n[ANALIZA SEGMENTÓW WIZUALNYCH]:\n${combinedAnalysis}`;

              // Synteza to zadanie CZYSTO TEKSTOWE — wyłączamy MCP tools (false),
              // żeby model nie próbował wywoływać vision_analyze_base64 ani innych
              // narzędzi zamiast pisać raport. Normalne czaty i agenci nadal używają mcpEnabled.
              const finalResult = await aiService.runAgentSession(
                synthesisPrompt,
                [],
                false, // ← brak tools dla syntezy — czyste zadanie tekstowe
                abortControllerRef.current?.signal,
                undefined, // nie ograniczamy serwerów, ale tools=false już wyklucza ich użycie
                maxContextTokens,
                synthMaxTok
              );

              setMessages(prev => [
                ...prev,
                { role: 'assistant', content: finalResult.text, rawContent: finalResult.rawText }
              ]);

              playResponse(finalResult.text);
              analyzeResponseForCode(finalResult.text);

            } catch (err: any) {
              console.error(err);
              addLog?.('ERROR', `VLM processing failed: ${err.message}`, 'VLM');
              setMessages(prev => [...prev, { role: 'system', content: `❌ Wystąpił błąd podczas orkiestracji wideo: ${err.message}` }]);
            } finally {
              setLoading(false);
            }
          }}
          onCancel={() => setVideoFileToProcess(null)}
        />
      )}
    </div>
  );
};

export default AgentChat;
